/**
 * Gossl Outfit Configurator v2
 * Fixed-position collage with proportional sizing and auto-grouping.
 * No dragging — curated magazine-style layout.
 *
 * ════════════════════════════════════════════════════════════════════
 *  KNOWN GOTCHAS — DO NOT REMOVE WITHOUT TESTING THE LISTED SCENARIO
 * ════════════════════════════════════════════════════════════════════
 *
 * These fixes have all been regressed at least once already. Before
 * deleting any code marked "// !! DO NOT REMOVE !!", verify the scenario
 * in the comment is still handled correctly. iOS Safari is the common
 * culprit — most of these were caught only on real iPhone testing.
 *
 *  1. iOS Safari faded share screenshot (commit 40e60d0, 2026-04-28)
 *     → showSharePreview() must pass `width / height / x / y /
 *       windowWidth` options to html2canvas, AND hide the actions
 *       buttons (NOT reposition the wrap). Removing any of these
 *       returns the faded-products bug.
 *
 *  2. Felix's _Artikel-Nr line item property (commit d156473, 2026-04-29)
 *     → Cart-add payload must include `properties._Artikel-Nr` from the
 *       product's `Artikelnr:` tag. Required by the Steyr/Bergheim
 *       store team to look up Intex articles in the Shopify backend.
 *
 *  3. /products/{handle}.json returns `available: null` for some products
 *     (cache / sales-channel quirk in Shopify).
 *     → Treat null/undefined as available — only mark sold-out on
 *       EXPLICIT `available === false`. Any check using `!v.available`
 *       falsely marks every variant of those products "ausverkauft".
 *
 *  4. Ensure window.Goessl is merged, never overwritten.
 *     (See goessl_window_goessl_merge memory.) Replacing the global
 *     wipes wishlist / quick-view / mega-menu hooks attached by other
 *     scripts.
 *
 *  5. canvas-item entrance animation (`canvasItemIn` 0.35s opacity 0→1)
 *     → Must complete BEFORE share screenshot capture. Currently we
 *       wait on image-load only — if a future change adds an animation
 *       lock, it should also be awaited.
 *
 * If you find yourself "cleaning up" any of the above, that means it
 * just looks redundant — not that it is. Run the configurator on a
 * real iPhone and try sharing before merging.
 * ════════════════════════════════════════════════════════════════════
 */
(function() {
  'use strict';

  var R2_UPLOAD_URL = 'https://goessl-r2-upload.g-ssl.workers.dev/upload';
  var SHORTLINK_CREATE_URL = 'https://share.goessl.com/s/create';
  var OG_CREATE_URL = 'https://share.goessl.com/o/create';
  var MAX_ITEMS = 7;

  // Money — Austrian suffix format ("350,00 €"), never "€350,00". Prices here
  // come from /collections/*/products.json as decimal-euro values, while
  // window.Goessl.formatMoney takes CENTS, so round on the way in.
  function money(euros) {
    var cents = Math.round(parseFloat(euros) * 100);
    if (!isFinite(cents)) cents = 0;
    if (window.Goessl && typeof window.Goessl.formatMoney === 'function') {
      return window.Goessl.formatMoney(cents);
    }
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }

  // !! Brevo correction-email rescue (2026-04-30) — DO NOT REMOVE !!
  // The 2026-04-30 launch newsletter + correction email shipped a CTA
  // pointing at the bare configurator URL (no ?l= param), so clicks land
  // on an empty canvas instead of the showcased outfit shown in the
  // email image. When a bare URL arrives from a Brevo tracking domain,
  // redirect to the showcased outfit so the email image and the landing
  // page match. Safe to remove once the email click traffic dies down
  // (~2 weeks after send).
  (function brevoCorrectionRescue() {
    try {
      var hasParams = /[?&](l|o|oid|add)=/.test(window.location.search);
      if (hasParams) return;
      var ref = document.referrer || '';
      var fromBrevo = /(sendib|brevosend|sendinblue)/i.test(ref);
      if (!fromBrevo) return;
      var rescueParam = '?l=eyJnIjoiZiIsImkiOlt7ImgiOiI5NTA1OS1rbGFzc2lrLWRpcm5kbCIsImMiOiJmcmF1ZW4tZGlybmRsIiwidiI6NTUzMzkxMzk4OTE1MzQsInMiOjEwMDAsImNjIjoiNjEiLCJ4IjoyNTAsInkiOjY5LCJ3Ijo0OTIsIkgiOjgzNH0seyJoIjoiOTUwNzYtZGlybmRsYmx1c2UtbWl0LWhhbmRzdGljay1kZXRhaWwiLCJjIjoiZnJhdWVuLWRpcm5kbGJsdXNlbiIsInYiOjU5MTU2MDA4MDc1NTk4LCJzIjo4NTAsIngiOjAsInkiOjQ0MywidyI6Mzg3LCJIIjo1MDB9LHsiaCI6Ijk1MDY3LWJhcm9jay1kcnVja3NjaHVlcnplIiwiYyI6ImZyYXVlbi1zY2h1ZXJ6ZW4iLCJ2Ijo1OTEyMTI4MDU0OTE5OCwicyI6ODUwLCJ4IjoxLCJ5IjoxMywidyI6NDA0LCJIIjo2NzJ9LHsiaCI6IjkzOTk5LWxlZGVydGFzY2hlIiwiYyI6ImZyYXVlbi10YXNjaGVuIiwidiI6NTUzMjA1NzY1MjQ2MjIsInMiOjYwMCwieCI6NjYxLCJ5Ijo0NTgsInciOjI3NywiSCI6Mzg3fSx7ImgiOiI5MTU4NS1pc2NobGVyLXdhbGtqYWNrZSIsImMiOiJmcmF1ZW4tamFja2VuIiwidiI6NTUzMTkxNjQyNTY1OTAsInMiOjk1MCwieCI6NjAyLCJ5Ijo0NiwidyI6MzkxLCJIIjo2MTZ9XX0';
      window.location.replace(window.location.pathname + rescueParam);
    } catch (e) { /* fail open — never block the page */ }
  })();

  // Short-URL cache: long URL → Promise resolving to short URL.
  // Same long URL never gets minted twice; QR re-render reuses the resolved value.
  var shortUrlCache = {};

  function mintShortUrl(longUrl) {
    if (shortUrlCache[longUrl]) return shortUrlCache[longUrl];
    // The shortlink endpoint is a Cloudflare Worker — cold starts plus CORS
    // preflight regularly take 5-7s on first hit, then drop to <1s once the
    // Worker is warm. We give the fetch 10s before giving up. Retry once on
    // failure; the second attempt almost always succeeds because the Worker
    // is now warm.
    function attempt(attemptNo) {
      var controller = new AbortController();
      var timer = setTimeout(function () { controller.abort(); }, 10000);
      return fetch(SHORTLINK_CREATE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: longUrl }),
        signal: controller.signal
      }).then(function (r) {
        clearTimeout(timer);
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      }).then(function (data) {
        if (data && data.ok && data.shortUrl) return data.shortUrl;
        throw new Error((data && data.error) || 'shortlink_failed');
      }).catch(function (err) {
        clearTimeout(timer);
        if (attemptNo < 2) {
          if (window.console) {
            console.warn('[outfit-configurator] short-URL mint attempt ' + attemptNo + ' failed (' +
              (err && err.message ? err.message : err) + ') — retrying.');
          }
          return attempt(attemptNo + 1);
        }
        if (window.console) {
          console.warn('[outfit-configurator] short-URL mint failed twice:',
            err && err.message ? err.message : err,
            '— QR will encode long URL (denser, but still scans).');
        }
        delete shortUrlCache[longUrl]; // allow retry on next outfit change
        return longUrl;
      });
    }
    shortUrlCache[longUrl] = attempt(1);
    return shortUrlCache[longUrl];
  }

  // OG-aware share URL: gsl.asksj.com/o/CODE that, when crawled by Facebook /
  // WhatsApp / Twitter / etc., returns HTML with the outfit screenshot as
  // og:image. Humans hitting the same URL get a 302 to the goessl.com
  // configurator with the outfit pre-loaded. Used for FB + Pinterest shares
  // so the link preview shows the actual outfit.
  function mintOgUrl(longUrl, imageUrl) {
    return fetch(OG_CREATE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        url: longUrl,
        image: imageUrl,
        title: window.g26tText('configurator.share_title', 'Mein Gössl Outfit'),
        description: window.g26t('configurator.share_og_description', 'Schauen Sie sich mein Gössl Outfit an.')
      })
    }).then(function(r) { return r.json(); }).then(function(data) {
      if (data && data.ok && data.shortUrl) return data.shortUrl;
      throw new Error((data && data.error) || 'og_url_failed');
    }).catch(function() {
      // On failure, return the long URL — share still works, just no rich preview.
      return longUrl;
    });
  }

  // Color family swatch hexes — kept in lockstep with the collection filter panel
  // (see .goessl-filter__color-swatch[data-color-family=...] in main-collection.liquid).
  // Key = first digit of Gössl color code; value = swatch hex used in the collection filters.
  var COLOR_FAMILIES = {
    '1': '#FFFFFF',  // Weiss
    '2': '#E8DCC8',  // Natur
    '3': '#C41E3A',  // Rot
    '4': '#2D5016',  // Grün
    '5': '#1B3A6B',  // Blau
    '6': '#E8A0BF',  // Rosa
    '7': '#333333',  // Grau & Schwarz
    '8': '#8B5E3C',  // Braun
    '9': '#C5A572',  // Gelb & Gold
    '0': '#aaaaaa'   // Sonstige (no equivalent in collection filter — fallback grey)
  };

  function getColorHex(colorCode) {
    if (!colorCode) return '#ccc';
    var first = String(colorCode).charAt(0);
    return COLOR_FAMILIES[first] || '#ccc';
  }

  function extractColorCode(optionValue) {
    if (!optionValue) return null;
    var match = optionValue.match(/\s(\d+)$/);
    return match ? match[1] : null;
  }

  // r39e (SJ): family from the COLOUR NAME, not the FarbNr's first digit —
  // the digit heuristic mis-files half the palette ("gebranntes Holz 77"
  // landed under Grau & Schwarz), which made the chips feel like they did
  // nothing. First colour word in the value wins ("blau-grau" → Blau);
  // the digit stays only as a last-resort fallback for unnamed values.
  var COLOR_WORD_FAMILIES = [
    [/wei(ß|ss)|wollwei|champagner|ecru|creme|elfenbein|perlmutt/, '1'],
    [/natur|beige|sand|taupe|camel|kitt|leinenfarb/, '2'],
    [/rot|bordeaux|wein|himbeer|kirsch|koralle|purpur/, '3'],
    [/gr(ü|ue)n|oliv|tanne|jagd|salbei|schilf|moos|loden/, '4'],
    [/blau|marine|denim|jeans|petrol/, '5'],
    [/rosa|ros(é|e´)|pink|altrosa|mauve|puder/, '6'],
    [/grau|schwarz|anthrazit|graphit|silber/, '7'],
    [/braun|holz|cognac|schoko|kastanie|mokka|nuss|tabak|rost|kupfer|hirschleder/, '8'],
    [/gelb|gold|senf|curry|honig|mais/, '9'],
  ];
  function colorFamilyOf(optionValue) {
    if (!optionValue) return null;
    var s = String(optionValue).toLowerCase();
    var best = null, bestIdx = Infinity;
    COLOR_WORD_FAMILIES.forEach(function(pair) {
      var m = s.search(pair[0]);
      if (m > -1 && m < bestIdx) { bestIdx = m; best = pair[1]; }
    });
    if (best) return best;
    var code = extractColorCode(optionValue);
    return code ? code.charAt(0) : null;
  }

  // The photo of one colour. r192: the colour's own variants decide, not the file name - Shopify Files
  // dedups by content hash, so a file can carry another colour's name (95162: the files named 602-1x are
  // the türkis 407 photos, the alt keeps the true name, and rosa 602 showed türkis). Order:
  //   1. the image the colour's variants point at (featured_image in products.json, image_id in
  //      /products/{handle}.json) - the colour's lead photo, set per colour since the 30.09 audit;
  //   2. an image whose alt carries -{colour}- (only /products/{handle}.json has alts), front -10 first;
  //   3. the file name ({article}-{colour}-{position}, -10 = front), for colours without either.
  function imageMatching(product, colorCode, textOf) {
    var pattern = '-' + colorCode + '-';
    var firstMatch = null;
    for (var i = 0; i < product.images.length; i++) {
      var text = textOf(product.images[i]) || '';
      if (text.indexOf(pattern) === -1) continue;
      if (!firstMatch) firstMatch = product.images[i];
      if (text.indexOf(pattern + '10') > -1) return product.images[i];
    }
    return firstMatch;
  }
  function findImageForColor(product, colorCode) {
    if (!product.images || !colorCode) return null;
    var vs = (product.variants || []).filter(function(v) { return extractColorCode(v.option1 || '') === colorCode; });
    for (var i = 0; i < vs.length; i++) {
      var f = vs[i].featured_image;
      var id = (f && f.id) || vs[i].image_id;
      if (!id) continue;
      var own = product.images.filter(function(im) { return String(im.id) === String(id); })[0];
      if (own) return own;
      if (f && f.src) return f;
    }
    return imageMatching(product, colorCode, function(im) { return im.alt; }) ||
      imageMatching(product, colorCode, function(im) { return im.src; });
  }
  // All photos of one colour (the card's hover cycle), its lead photo first. Alts decide where they exist;
  // a file name only when no lead photo contradicts it - otherwise the colour keeps its lead photo alone.
  function imagesForColor(product, colorCode, lead) {
    var pattern = '-' + colorCode + '-';
    var imgs = product.images || [];
    var list;
    if (imgs.some(function(im) { return !!im.alt; })) {
      list = imgs.filter(function(im) { return (im.alt || '').indexOf(pattern) > -1; });
    } else {
      var clash = !!lead && lead.src.indexOf(pattern) === -1;
      (product.variants || []).forEach(function(v) {
        var f = v.featured_image;
        if (f && f.src && f.src.indexOf(pattern) > -1 && extractColorCode(v.option1 || '') !== colorCode) clash = true;
      });
      list = clash ? [] : imgs.filter(function(im) { return im.src.indexOf(pattern) > -1; });
    }
    if (lead) list = [lead].concat(list.filter(function(im) { return String(im.id) !== String(lead.id); }));
    return list;
  }

  // Pick the best card image for a product. Intex naming convention:
  //   -10 = front (preferred)
  //   -11 = back (centered, decent fallback)
  //   -19 = alternate full-body
  //   -12..-18 = details (often cropped/zoomed — last resort)
  // We walk the ladder in that order so we never show a detail shot
  // when a front/back/alt is available, even if upstream image
  // ordering puts the detail at index 0 (e.g. 95054-walkjacke,
  // which has no -10 at all).
  function findFrontImage(product) {
    if (!product.images || !product.images.length) return null;
    var matchSuffix = function(suffix) {
      var re = new RegExp('-\\d{1,4}-' + suffix + '[._]');
      for (var i = 0; i < product.images.length; i++) {
        if (re.test(product.images[i].src)) return product.images[i];
      }
      return null;
    };
    return matchSuffix('10') || matchSuffix('11') || matchSuffix('19') || product.images[0];
  }

  // Size ordering (matches product-form.js)
  var SIZE_ORDER = [
    'XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL',
    '0', '1', '2', '3', '4', '4+', '5', '5+', '6', '6+',
    '26', '27', '28', '29', '30', '31', '32', '33', '34', '35', '36', '37', '38',
    '39', '40', '41', '42', '44', '46', '48', '50', '52', '54', '56', '58', '60',
    '62', '64', '86', '92', '98', '104', '110', '116', '122', '128', '134', '140',
    '146', '152', '158', '164', '170', '176'
  ];

  // ═══════════════════════════════════════════
  //  CATEGORY SIZES + DYNAMIC GRID LAYOUTS
  // ═══════════════════════════════════════════

  // Size scale per category (1.0 = full cell, smaller = scaled down within cell)
  var CATEGORY_SIZES = {
    'dirndl': 1.0, 'kleider': 1.0, 'roecke': 0.85,
    'lederhosen': 1.0, 'hosen': 0.9,
    'blusen': 0.85, 'dirndlblusen': 0.85, 'hemden': 0.85,
    'jacken': 0.95, 'maentel': 0.95, 'strick': 0.85, 'westen': 0.85, 'gilets': 0.85,
    'schuerzen': 0.85, 'tuecher-schals': 0.6, 'krawatten': 0.5, 'stecktucher': 0.5,
    'schuhe': 0.55, 'stutzen': 0.5,
    'taschen': 0.6, 'huete': 0.55, 'accessoires': 0.5, 'guertel': 0.45
  };

  function getCategorySize(collection) {
    var subcat = collection.replace(/^(frauen|maenner|kinder)-/, '');
    return CATEGORY_SIZES[subcat] || 0.7;
  }

  // Grid positions for N items. Cells overlap ~4% for tight nestling.
  // Items sorted by size — position[0] is always the hero (biggest item).
  // Each cell: { x, y, w, h } as fractions of canvas.
  var GRID_LAYOUTS = {
    1: [
      { x: 0.25, y: 0.02, w: 0.50, h: 0.90 }
    ],
    2: [
      { x: 0.20, y: 0.02, w: 0.45, h: 0.90 },
      { x: 0.50, y: 0.08, w: 0.45, h: 0.84 }
    ],
    // Diagonal: hero center, #2 upper-left, #3 lower-right
    3: [
      { x: 0.28, y: 0.08, w: 0.44, h: 0.76 },
      { x: 0.00, y: 0.00, w: 0.38, h: 0.52 },
      { x: 0.58, y: 0.40, w: 0.42, h: 0.56 }
    ],
    // 2x2 stagger: hero top-left, #2 top-right offset down, #3 bottom-left, #4 bottom-right offset
    4: [
      { x: 0.00, y: 0.00, w: 0.46, h: 0.56 },
      { x: 0.50, y: 0.06, w: 0.46, h: 0.54 },
      { x: 0.04, y: 0.46, w: 0.44, h: 0.54 },
      { x: 0.52, y: 0.50, w: 0.44, h: 0.50 }
    ],
    // 3 top + 2 bottom, overlapping
    5: [
      { x: 0.28, y: 0.00, w: 0.44, h: 0.62 },
      { x: 0.00, y: 0.02, w: 0.36, h: 0.50 },
      { x: 0.62, y: 0.02, w: 0.38, h: 0.50 },
      { x: 0.04, y: 0.44, w: 0.38, h: 0.56 },
      { x: 0.55, y: 0.46, w: 0.42, h: 0.54 }
    ],
    // 3x2 stagger with overlap
    6: [
      { x: 0.28, y: 0.00, w: 0.40, h: 0.58 },
      { x: 0.00, y: 0.00, w: 0.36, h: 0.54 },
      { x: 0.62, y: 0.00, w: 0.38, h: 0.54 },
      { x: 0.00, y: 0.46, w: 0.36, h: 0.54 },
      { x: 0.30, y: 0.50, w: 0.38, h: 0.50 },
      { x: 0.62, y: 0.46, w: 0.38, h: 0.54 }
    ],
    // 4 top + 3 bottom, staggered
    7: [
      { x: 0.18, y: 0.00, w: 0.34, h: 0.52 },
      { x: 0.00, y: 0.02, w: 0.26, h: 0.46 },
      { x: 0.44, y: 0.02, w: 0.28, h: 0.46 },
      { x: 0.68, y: 0.00, w: 0.32, h: 0.48 },
      { x: 0.04, y: 0.42, w: 0.34, h: 0.56 },
      { x: 0.32, y: 0.46, w: 0.34, h: 0.52 },
      { x: 0.60, y: 0.42, w: 0.36, h: 0.56 }
    ],
    // 4 top + 4 bottom, staggered
    8: [
      { x: 0.18, y: 0.00, w: 0.32, h: 0.50 },
      { x: 0.00, y: 0.02, w: 0.26, h: 0.44 },
      { x: 0.44, y: 0.02, w: 0.26, h: 0.44 },
      { x: 0.68, y: 0.00, w: 0.30, h: 0.46 },
      { x: 0.04, y: 0.42, w: 0.28, h: 0.52 },
      { x: 0.26, y: 0.46, w: 0.28, h: 0.48 },
      { x: 0.48, y: 0.42, w: 0.28, h: 0.52 },
      { x: 0.70, y: 0.44, w: 0.30, h: 0.50 }
    ]
  };

  // ═══════════════════════════════════════════
  //  STATE
  // ═══════════════════════════════════════════

  var productCache = {};

  // ── Vorbestellung (pre-order) ────────────────────────────────────────────
  // A pre-order line must carry the selling plan, or it books as an ordinary
  // oversold sale: no "Vorbestellung" on the confirmation, nothing in the admin
  // marking it as awaiting goods, and a customer who was never told they are
  // waiting. The configurator's source (/collections/*/products.json) carries
  // `tags` but NOT selling_plan_groups, so the plan id has to come from
  // the product itself — asked only for products actually tagged
  // `vorbestellung`, so an ordinary outfit add makes no extra request at all.
  // r187: window.g26preorder (layout/theme.liquid, ?view=vorrat) answers per
  // size — a size with stock sells normally and goes in without the plan — and
  // matches the plan by ID (the group name reads "Pre-order" on /en, so the old
  // name match added every English pre-order line without the plan).
  function isPreorderHandle(handle) {
    var p = productCache[handle];
    return !!(p && p.tags && p.tags.indexOf('vorbestellung') !== -1);
  }

  // r188: after an add, never more pieces without the plan than the size has in stock
  // (assets/preorder-cart.js) - only when the outfit holds a pre-order piece.
  function capPreorderLines(handles) {
    var any = (handles || []).some(function (h) { return h && isPreorderHandle(h); });
    return any && window.g26preorder && window.g26preorder.capCart ? window.g26preorder.capCart() : Promise.resolve();
  }

  // Resolves the plan for every pre-order line in `cartItems` (handles[i] belongs
  // to cartItems[i]) and writes selling_plan onto the lines that need it.
  // Fail open: a line without the plan still reaches the cart. Losing the
  // pre-order marking is bad; losing the sale outright is worse.
  function attachPreorderPlans(cartItems, handles) {
    return Promise.all(handles.map(function (h) {
      return h && isPreorderHandle(h) && window.g26preorder ? window.g26preorder.state(h) : null;
    })).then(function (states) {
      states.forEach(function (s, i) {
        var planId = cartItems[i] && window.g26preorder ? window.g26preorder.planFor(s, cartItems[i].id) : null;
        if (planId) cartItems[i].selling_plan = planId;
      });
      return cartItems;
    });
  }
  // Unified canvas state — items from any gender (Frauen/Männer/Kinder) live in one composition
  var canvasState = [];
  // currentGender is derived from the most-recently-selected category prefix.
  // Kept for save metadata + share-URL back-compat. UI no longer exposes a gender toggle.
  var currentGender = 'frauen';
  // Set when the page is opened against a saved outfit (?oid=…) so subsequent
  // saves update that record instead of creating a new one.
  var currentOutfitId = null;
  var undoStack = []; // snapshots of state for undo
  var MAX_UNDO = 15;
  // r191: the guided path (GUIDED PATH section below). path/step index into the section's data-guide-config;
  // step === steps.length is the last step (sizes, cart, Merkliste).
  var guide = {
    cfg: null, root: null, el: null, active: false, path: 0, step: 0,
    chip: {}, allLen: {}, family: {}, skipped: {}, cuts: {}, optionsKey: '', freeEmpty: '',
    showMissing: false, status: '', statusSig: '', focusKey: ''
  };

  // Color family labels — must match the collection filter panel's data-color-family values
  var COLOR_FAMILY_NAMES = {
    '1': window.g26t('general.color_families.white', 'Weiss'), '2': window.g26t('general.color_families.natural', 'Natur'), '3': window.g26t('general.color_families.red', 'Rot'), '4': window.g26t('general.color_families.green', 'Grün'),
    '5': window.g26t('general.color_families.blue', 'Blau'), '6': window.g26t('general.color_families.pink', 'Rosa'), '7': window.g26t('general.color_families.grey_black', 'Grau & Schwarz'), '8': window.g26t('general.color_families.brown', 'Braun'),
    '9': window.g26t('general.color_families.yellow_gold', 'Gelb & Gold'), '0': window.g26t('general.color_families.other', 'Sonstige')
  };

  // DOM refs
  var canvasItems, canvasEmpty, totalEl, saveBtn, cartBtn, shareBtn, stickyFooter, resetBtn, clearBtn, undoBtn;
  var productGrid; // shared product grid element
  var filterBar; // filter chips container
  var activeFilters = []; // active color family codes
  var activeSizes = [];   // r112: active size values (option 'Größe')
  var activeLengths = []; // r112: active Länge values (digits, e.g. '80')
  var gridCache = {}; // collection → DocumentFragment cache
  // Selected-items panel refs
  var selectedList, selectedCount, selectedTotal, selectedCart, selectedEmpty;

  function getState() { return canvasState; }
  function setState(items) { canvasState = items; }

  // ═══════════════════════════════════════════
  //  INIT
  // ═══════════════════════════════════════════

  function init() {
    canvasItems = document.querySelector('[data-canvas-items]');
    canvasEmpty = document.querySelector('[data-canvas-empty]');
    totalEl = document.querySelector('[data-outfit-total]');
    saveBtn = document.querySelector('[data-outfit-save]');
    shareBtn = document.querySelector('[data-outfit-share]');
    cartBtn = document.querySelector('[data-outfit-cart]');
    stickyFooter = document.querySelector('[data-outfit-footer]');
    productGrid = document.querySelector('[data-product-grid]');
    filterBar = document.querySelector('[data-outfit-filters]');
    resetBtn = document.querySelector('[data-reset-positions]');
    clearBtn = document.querySelector('[data-clear-canvas]');
    selectedList = document.querySelector('[data-selected-list]');
    selectedCount = document.querySelector('[data-selected-count]');
    selectedTotal = document.querySelector('[data-selected-total]');
    selectedCart = document.querySelector('[data-selected-cart]');

    if (!canvasItems) return;

    if (selectedCart) {
      selectedCart.addEventListener('click', function() {
        if (!getState().length) {
          showNotice(window.g26t('configurator.notice_empty_title', 'Canvas ist leer'), window.g26t('configurator.notice_empty_add_first', 'Bitte fügen Sie zuerst Stücke aus den Kategorien hinzu.'));
          return;
        }
        var allSized = getState().every(function(it) { return it.variant_id; });
        if (allSized) {
          submitInlineCart();
        } else {
          openSizeModal();
        }
      });
    }

    if (resetBtn) {
      resetBtn.addEventListener('click', function() { resetPositions(); });
    }
    if (clearBtn) {
      clearBtn.addEventListener('click', function() { clearCanvas(); });
    }
    undoBtn = document.querySelector('[data-undo-canvas]');
    if (undoBtn) {
      undoBtn.addEventListener('click', function() { undo(); });
    }

    // QR is intentionally NOT draggable (per SJ): it's a fixed branded
    // element pinned to the bottom-centre of the canvas. Same for the
    // brand mark at the top-centre. Both must stay anchored so the
    // share screenshot is always composed correctly.

    // Generate initial QR code with current share URL
    updateCanvasQR();

    // Click empty canvas space to deselect all items
    var canvasEl = document.querySelector('[data-outfit-canvas]');
    if (canvasEl) {
      canvasEl.addEventListener('click', function(e) {
        if (!e.target.closest('.outfit-cfg__canvas-item')) {
          canvasItems.querySelectorAll('.outfit-cfg__canvas-item.is-tapped').forEach(function(c) {
            c.classList.remove('is-tapped');
          });
        }
      });
    }

    bindGenderToggle();
    bindCategorySelects();
    buildCatDropdowns();
    bindBottomBar();
    guideInit(); // r191: before loadFromUrl, so a shared or saved outfit opens in the guided path
    loadFromUrl();

    // Auto-load first category from the combined select
    var firstSelect = document.querySelector('.outfit-cfg__cat-select');
    if (firstSelect && firstSelect.value) showProductGrid(firstSelect.value);
  }

  // ═══════════════════════════════════════════
  //  GENDER TOGGLE — removed; gender is now derived from selected category
  // ═══════════════════════════════════════════

  function bindGenderToggle() {
    // No-op: the gender toggle UI was removed in favour of a single combined dropdown.
    // currentGender is updated whenever a category is selected (see bindCategorySelects).
  }

  // ═══════════════════════════════════════════
  //  CATEGORY DROPDOWN — single combined select with optgroups
  // ═══════════════════════════════════════════

  /* r69 (SJ): the OS select popup clashes with the theme. A styled listbox
     proxies the (visually hidden) native <select>, which stays the single
     source of truth for every .value read across the configurator. */
  function buildCatDropdowns() {
    document.querySelectorAll('.outfit-cfg__cat-select-wrap').forEach(function (wrap) {
      var select = wrap.querySelector('.outfit-cfg__cat-select');
      if (!select || wrap.querySelector('.outfit-cfg__cat-btn')) return;
      select.classList.add('outfit-cfg__cat-select--proxied');
      select.setAttribute('tabindex', '-1');
      select.setAttribute('aria-hidden', 'true');

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'outfit-cfg__cat-btn';
      btn.setAttribute('aria-haspopup', 'listbox');
      btn.setAttribute('aria-expanded', 'false');

      var panel = document.createElement('div');
      panel.className = 'outfit-cfg__cat-panel';
      panel.setAttribute('role', 'listbox');
      panel.hidden = true;

      function labelOf() {
        var o = select.options[select.selectedIndex];
        return o ? o.textContent : window.g26t('products.product.category', 'Kategorie');
      }
      function syncBtn() {
        btn.innerHTML = '<span>' + labelOf() + '</span>' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>';
      }
      function addOpt(opt) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'outfit-cfg__cat-opt' + (opt.value === select.value ? ' is-active' : '');
        b.setAttribute('role', 'option');
        b.setAttribute('aria-selected', opt.value === select.value ? 'true' : 'false');
        b.textContent = opt.textContent;
        b.addEventListener('click', function () {
          select.value = opt.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          syncBtn(); close();
        });
        panel.appendChild(b);
      }
      function rebuild() {
        panel.replaceChildren();
        Array.prototype.forEach.call(select.children, function (node) {
          if (node.tagName === 'OPTGROUP') {
            var h = document.createElement('p');
            h.className = 'outfit-cfg__cat-group';
            h.textContent = node.label;
            panel.appendChild(h);
            Array.prototype.forEach.call(node.children, addOpt);
          } else if (node.tagName === 'OPTION') {
            addOpt(node);
          }
        });
      }
      function open() { rebuild(); panel.hidden = false; btn.setAttribute('aria-expanded', 'true'); }
      function close() { panel.hidden = true; btn.setAttribute('aria-expanded', 'false'); }
      btn.addEventListener('click', function () { if (panel.hidden) open(); else close(); });
      document.addEventListener('click', function (e) { if (!wrap.contains(e.target)) close(); });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
      select.addEventListener('change', syncBtn);
      syncBtn();
      wrap.appendChild(btn);
      wrap.appendChild(panel);
      wrap.classList.add('outfit-cfg__cat-select-wrap--dd');
    });
  }

  function bindCategorySelects() {
    document.querySelectorAll('.outfit-cfg__cat-select').forEach(function(select) {
      select.addEventListener('change', function() {
        // Derive currentGender from the selected category's prefix
        if (select.value.indexOf('frauen-') === 0) currentGender = 'frauen';
        else if (select.value.indexOf('maenner-') === 0) currentGender = 'maenner';
        else if (select.value.indexOf('kinder-') === 0) currentGender = 'kinder';
        showProductGrid(select.value);
      });
    });
  }

  function showProductGrid(collection) {
    if (!productGrid) return;

    // Reset filters when switching categories
    activeFilters = []; activeSizes = []; activeLengths = [];

    // Check cache
    if (gridCache[collection]) {
      productGrid.replaceChildren();
      gridCache[collection].forEach(function(card) {
        card.hidden = false;
        productGrid.appendChild(card);
      });
      // Rebuild filters from cached products
      var cachedProducts = gridCache[collection].map(function(card) {
        return productCache[card.dataset.handle];
      }).filter(Boolean);
      buildFilters(cachedProducts);
      highlightSelectedCards();
      applyFilters();
      return;
    }

    // Show loading
    productGrid.replaceChildren();
    var loading = document.createElement('p');
    loading.className = 'outfit-cfg__loading';
    loading.textContent = window.g26tText('general.accessibility.loading', 'Wird geladen...');
    productGrid.appendChild(loading);
    gridShowing = collection;

    loadCollection(collection).then(function(products) {
      if (gridShowing !== collection) return; // another category was chosen meanwhile
      if (!products.length) {
        productGrid.replaceChildren();
        var empty = document.createElement('p');
        empty.className = 'outfit-cfg__loading';
        empty.textContent = window.g26tText('configurator.no_products', 'Keine Produkte gefunden.');
        productGrid.appendChild(empty);
        return;
      }
      var cards = [];
      products.forEach(function(product) {
        // Skip products with no images — the configurator is image-driven, an empty card is broken UX
        if (!product.images || !product.images.length) return;
        // r112 (SJ 01.09): one card per COLOUR, like the collection pages — a deep link or a
        // click always carries an explicit colour, so the canvas never falls back to variant #1.
        var picks = colourPicksFor(product);
        if (picks.length > 1) picks.forEach(function(pk) { cards.push(createMiniCard(product, collection, pk)); });
        else cards.push(createMiniCard(product, collection, null));
      });
      gridCache[collection] = cards;
      productGrid.replaceChildren();
      cards.forEach(function(card) {
        productGrid.appendChild(card);
      });
      buildFilters(products);
      highlightSelectedCards();
      applyFilters();
    }).catch(function() {
      if (gridShowing !== collection) return;
      productGrid.replaceChildren();
      var err = document.createElement('p');
      err.className = 'outfit-cfg__loading';
      err.textContent = window.g26tText('configurator.load_error', 'Fehler beim Laden.');
      productGrid.appendChild(err);
    });
  }

  var gridShowing = null;
  var collectionLoads = {};

  // One category's products in the order the collection page shows them, cached per category - the free
  // grid and the guided steps share it (r191), so a step never fetches what the grid already has.
  function loadCollection(collection) {
    if (collectionLoads[collection]) return collectionLoads[collection];
    // Two parallel fetches:
    //   1. products.json → product data (price, images, variants, etc.)
    //   2. collection HTML → display order (Shopify's products.json ignores
    //      the collection's manual sort, always returns created-desc).
    // Then reorder the products from #1 to match the handle sequence in #2,
    // so the configurator grid mirrors what the customer sees on the live
    // collection page (pinned-top → newest+best → sale-bottom from task #161).
    collectionLoads[collection] = Promise.all([
      fetch(window.g26url('collections/' + collection + '/products.json') + '?limit=48&_fd=0').then(function(r) { return r.ok ? r.json() : null; }),
      fetch(window.g26url('collections/' + collection) + '?_fd=0').then(function(r) { return r.ok ? r.text() : ''; }).catch(function() { return ''; })
    ]).then(function(results) {
      var data = results[0];
      var html = results[1];
      if (!data || !data.products) throw new Error('products.json ' + collection);
      if (!data.products.length) return [];

      // Build {handle -> { weight, onSale, idx }} from the product cards in
      // the HTML. Two important details:
      //
      // 1. SCOPE the regex to <article class="goessl-product-card"> blocks —
      //    a bare /\/products\/.../ regex picks up the mega-menu featured
      //    product (which appears BEFORE the grid in source order) and ranks
      //    it first in the configurator. Only the actual cards belong here.
      //
      // 2. The live collection page applies a client-side resort
      //    (sale-to-bottom, then season-weight DESC) AFTER initial render.
      //    The SSR card sequence is just Shopify's underlying collection
      //    order — to match what users actually see we have to apply the
      //    same resort using each card's data-on-sale + data-season-weight.
      var orderMap = {};
      if (html) {
        var cardPattern = /<article[^>]*class="[^"]*goessl-product-card[^"]*"([\s\S]*?)<\/article>/g;
        var cardMatch;
        var cardIdx = 0;
        while ((cardMatch = cardPattern.exec(html)) !== null) {
          var card = cardMatch[0];
          var hrefMatch = card.match(/\/products\/([a-z0-9-]+)/);
          if (!hrefMatch) continue;
          var h = hrefMatch[1];
          if (h in orderMap) continue;
          var weightMatch = card.match(/data-season-weight="(\d+)"/);
          var saleMatch = card.match(/data-on-sale="(true|false)"/);
          orderMap[h] = {
            weight: weightMatch ? parseInt(weightMatch[1], 10) : 0,
            onSale: saleMatch ? saleMatch[1] === 'true' : false,
            idx: cardIdx++
          };
        }
      }

      // Stable resort: matches collection-page sort exactly.
      //   1. on-sale → bottom
      //   2. higher season-weight first
      //   3. fall back to source order (idx)
      // Products not in the SSR snapshot (newer than the page) go to the end.
      data.products.sort(function(a, b) {
        var ma = orderMap[a.handle];
        var mb = orderMap[b.handle];
        if (!ma && !mb) return 0;
        if (!ma) return 1;
        if (!mb) return -1;
        if (ma.onSale !== mb.onSale) return ma.onSale ? 1 : -1;
        if (ma.weight !== mb.weight) return mb.weight - ma.weight;
        return ma.idx - mb.idx;
      });

      data.products.forEach(function(product) {
        if (product.images && product.images.length) productCache[product.handle] = product;
      });
      return data.products;
    });
    collectionLoads[collection].catch(function() { delete collectionLoads[collection]; }); // retry on the next visit
    return collectionLoads[collection];
  }

  // Ticks every card of a piece that is on the canvas - in the free grid and in the guided step (r191).
  function highlightSelectedCards() {
    var sel = {};
    getState().forEach(function(item) { sel[item.handle] = item.colorCode || '*'; });
    [productGrid, guide.el && guide.el.options].forEach(function(root) {
      if (!root) return;
      root.querySelectorAll('.goessl-product-card').forEach(function(card) {
        var want = sel[card.dataset.handle];
        var on = want !== undefined && (!card.dataset.colorCode || want === '*' || want === card.dataset.colorCode);
        card.classList.toggle('is-selected', on);
      });
    });
  }

  // ═══════════════════════════════════════════
  //  COLOR FILTERS
  // ═══════════════════════════════════════════

  function buildFilters(products) {
    if (!filterBar) return;

    // Collect unique color families from loaded products
    var families = {};
    products.forEach(function(product) {
      (product.variants || []).forEach(function(v) {
        var family = colorFamilyOf(v.option1);
        if (family) families[family] = true;
      });
    });

    // Always show the full family palette (matches the collection filter sidebar).
    // Families that aren't present in this category are still rendered, with a
    // dimmed/disabled appearance via .is-empty so the row stays consistent.
    var ALL_FAMILIES = ['1','2','3','4','5','6','7','8','9'];
    var familyKeys = ALL_FAMILIES.slice();
    var familyHasProducts = function(k) { return !!families[k]; };
    if (familyKeys.length < 2) {
      filterBar.hidden = true;
      return;
    }

    // Clear existing chips (keep "Alle")
    var allBtn = filterBar.querySelector('[data-filter-all]');
    filterBar.replaceChildren();
    if (allBtn) {
      filterBar.appendChild(allBtn);
    } else {
      allBtn = document.createElement('button');
      allBtn.type = 'button';
      allBtn.className = 'outfit-cfg__filter-chip is-active';
      allBtn.dataset.filterAll = '';
      allBtn.textContent = window.g26tText('configurator.filter_all', 'Alle');
      filterBar.appendChild(allBtn);
    }

    allBtn.addEventListener('click', function() {
      activeFilters = []; activeSizes = []; activeLengths = [];
      filterBar.querySelectorAll('.outfit-cfg__filter-chip').forEach(function(c) {
        c.classList.remove('is-active');
      });
      allBtn.classList.add('is-active');
      applyFilters();
    });
    var syncAll = function() { allBtn.classList.toggle('is-active', !activeFilters.length && !activeSizes.length && !activeLengths.length); };

    familyKeys.forEach(function(family) {
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'outfit-cfg__filter-chip';
      chip.dataset.filterFamily = family;

      var dot = document.createElement('span');
      dot.className = 'outfit-cfg__filter-dot';
      dot.style.backgroundColor = COLOR_FAMILIES[family] || '#ccc';
      chip.appendChild(dot);

      // Bubble-only — name shown as native title tooltip. G26_I18N carries `| t` output, which escapes
      // "Grau & Schwarz" to "Grau &amp; Schwarz" - the tooltip and the screen reader got the entity.
      var familyName = decodeEntities(COLOR_FAMILY_NAMES[family] || family);
      chip.title = familyName;
      chip.setAttribute('aria-label', familyName);

      // Dim chips for families with no products in this category (still clickable but visually muted)
      if (!familyHasProducts(family)) chip.classList.add('is-empty');

      chip.addEventListener('click', function() {
        var idx = activeFilters.indexOf(family);
        if (idx > -1) {
          activeFilters.splice(idx, 1);
          chip.classList.remove('is-active');
        } else {
          activeFilters.push(family);
          chip.classList.add('is-active');
        }

        // Update "Alle" state
        syncAll();
        applyFilters();
      });

      filterBar.appendChild(chip);
    });

    activeFilters = []; activeSizes = []; activeLengths = [];

    // r112 (SJ 01.09): Größe + Länge chips next to the colours
    var sizeSet = {};
    products.forEach(function(product) {
      (product.variants || []).forEach(function(v) {
        if (v.available === false) return;
        var sz = sizeOfVariant(product, v);
        if (sz) sizeSet[sz] = true;
      });
    });
    var sizes = Object.keys(sizeSet).sort(function(a, b) { return sizeSortKey(a) - sizeSortKey(b); });
    var textChip = function(kind, value, label) {
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'outfit-cfg__filter-chip outfit-cfg__filter-chip--text';
      chip.dataset['filter' + kind] = value;
      chip.textContent = label;
      chip.addEventListener('click', function() {
        var arr = kind === 'Size' ? activeSizes : activeLengths;
        var idx = arr.indexOf(value);
        if (idx > -1) { arr.splice(idx, 1); chip.classList.remove('is-active'); }
        else { arr.push(value); chip.classList.add('is-active'); }
        syncAll();
        applyFilters();
      });
      return chip;
    };
    if (sizes.length > 1) {
      var sep = document.createElement('span'); sep.className = 'outfit-cfg__filter-sep'; sep.setAttribute('aria-hidden', 'true'); filterBar.appendChild(sep);
      var sizeGroup = document.createElement('span'); sizeGroup.className = 'outfit-cfg__filter-group'; sizeGroup.dataset.filterGroup = 'size';
      sizes.forEach(function(sz) { sizeGroup.appendChild(textChip('Size', sz, sz)); });
      filterBar.appendChild(sizeGroup);
    }
    // Länge: tag values now, metafield values once they have been fetched (Dirndl/Schürzen/Kleider/Röcke)
    var lenGroup = document.createElement('span'); lenGroup.className = 'outfit-cfg__filter-group'; lenGroup.dataset.filterGroup = 'length';
    var lenSep = document.createElement('span'); lenSep.className = 'outfit-cfg__filter-sep'; lenSep.setAttribute('aria-hidden', 'true'); lenSep.hidden = true;
    filterBar.appendChild(lenSep); filterBar.appendChild(lenGroup);
    var renderLengthChips = function() {
      var lenSet = {};
      products.forEach(function(product) {
        var t = normLen(getDirndlLength(product));
        if (!t && window.__goesslLenCache) t = normLen(window.__goesslLenCache[product.handle]);
        if (t) lenSet[t] = true;
      });
      var lens = Object.keys(lenSet).sort(function(a, b) { return parseInt(a, 10) - parseInt(b, 10); });
      lenGroup.replaceChildren();
      lens.forEach(function(l) { var c = textChip('Length', l, l + ' cm'); if (activeLengths.indexOf(l) > -1) c.classList.add('is-active'); lenGroup.appendChild(c); });
      lenSep.hidden = lens.length < 2; lenGroup.hidden = lens.length < 2;
    };
    renderLengthChips();
    var currentSelect = document.querySelector('.outfit-cfg__cat-select-wrap:not([hidden]) .outfit-cfg__cat-select');
    if (currentSelect && /(dirndl|schuerzen|kleider|roecke)/.test(currentSelect.value || '')) {
      Promise.all(products.map(function(p) { return fetchLaenge(p.handle); })).then(function() {
        // cards may have rendered before their metafield arrived — carry the value onto them
        productGrid.querySelectorAll('.goessl-product-card').forEach(function(card) {
          if (!card.dataset.laenge && window.__goesslLenCache) { var lv = normLen(window.__goesslLenCache[card.dataset.handle]); if (lv) card.dataset.laenge = lv; }
        });
        renderLengthChips();
      });
    }
    filterBar.hidden = false;
  }

  // ═══════════════════════════════════════════
  //  LENGTH COMPATIBILITY
  // ═══════════════════════════════════════════

  // Extract dirndl-length:XX tags from a product's tag string
  function getDirndlLength(product) {
    if (!product || !product.tags) return null;
    var tags = typeof product.tags === 'string' ? product.tags.split(', ') : product.tags;
    for (var i = 0; i < tags.length; i++) {
      var match = tags[i].match(/^dirndl-length[:\-](\d+)/i);
      if (match) return match[1];
    }
    return null;
  }

  // Länge via the product.laenge alternate template (metafield — not present in
  // /products/{handle}.json). Shared promise-cache with the Auswahl panel; a request still on its way
  // is shared too (r192: the length rule asks for the same pieces as the cards and the panel).
  var lenInflight = {};
  function fetchLaenge(handle) {
    if (!window.__goesslLenCache) window.__goesslLenCache = {};
    var lc = window.__goesslLenCache;
    if (Object.prototype.hasOwnProperty.call(lc, handle)) return Promise.resolve(lc[handle]);
    if (lenInflight[handle]) return lenInflight[handle];
    lenInflight[handle] = fetch(window.g26url('products/' + encodeURIComponent(handle)) + '?view=laenge&_fd=0')
      .then(function (r) { return r.ok ? r.text() : '""'; })
      .then(function (t) {
        var v = '';
        try { v = JSON.parse(t.trim()); } catch (e) {}
        lc[handle] = (typeof v === 'string' && v.trim()) ? v.trim() : '';
        return lc[handle];
      })
      .catch(function () { lc[handle] = ''; return ''; })
      .then(function (v) { delete lenInflight[handle]; return v; });
    return lenInflight[handle];
  }

  // r192: the Länge a piece shows on its card - the dirndl-length tag, else the goessl.dirndl_length
  // metafield through fetchLaenge's cache (the card badge's order; the two never disagree on 05.10).
  // Products created since late August carry only the metafield (95503, 95511-95516), and a tag-only
  // rule let them past every Länge. Not fetched yet: '' for now, fetched once, and the grid and the
  // guided step are filtered again when it arrives.
  var lenRequested = {}, lenRefreshTimer = 0;
  function pieceLength(product) {
    if (!product) return '';
    var t = normLen(getDirndlLength(product));
    if (t) return t;
    var lc = window.__goesslLenCache;
    if (lc && Object.prototype.hasOwnProperty.call(lc, product.handle)) return normLen(lc[product.handle]);
    if (!lenRequested[product.handle]) {
      lenRequested[product.handle] = true;
      fetchLaenge(product.handle).then(function (v) { if (normLen(v)) scheduleLengthRefresh(); });
    }
    return '';
  }
  function scheduleLengthRefresh() {
    if (lenRefreshTimer) return;
    lenRefreshTimer = setTimeout(function () {
      lenRefreshTimer = 0;
      applyFilters();
      if (guide.active && guide.cfg && !onSizesStep()) renderGuideOptions();
    }, 50);
  }

  var LENGTH_SENSITIVE = ['dirndl', 'schuerzen', 'kleider', 'roecke'];
  function lengthCategory(collection) {
    return String(collection || '').replace(/^(frauen|maenner|kinder)-/, '');
  }
  function isLengthSensitive(collection) {
    return LENGTH_SENSITIVE.indexOf(lengthCategory(collection)) !== -1;
  }

  // Check if a product is length-compatible with items on canvas.
  // Only applies to the length-sensitive categories above — all other categories always pass.
  // r192: a canvas piece of the grid's own category does not count, it is the piece being swapped -
  // a 70 cm Dirndl narrows the Schürzen grid to 70 cm, but the Dirndl grid keeps every length.
  function isLengthCompatible(product, currentCollection) {
    // Only filter length-sensitive categories
    if (!isLengthSensitive(currentCollection)) return true;

    var productLength = pieceLength(product);
    if (!productLength) return true; // No Länge = show it

    // Check canvas items for a length constraint
    var ownCategory = lengthCategory(currentCollection);
    var state = getState();
    for (var i = 0; i < state.length; i++) {
      if (lengthCategory(state[i].collection) === ownCategory) continue;
      var canvasProduct = productCache[state[i].handle];
      if (!canvasProduct) continue;
      var canvasLength = pieceLength(canvasProduct);
      if (canvasLength && canvasLength !== productLength) return false;
    }
    return true;
  }

  function applyFilters() {
    if (!productGrid) return;
    var cards = productGrid.querySelectorAll('.goessl-product-card');
    // Get current collection from dropdown
    var currentSelect = document.querySelector('.outfit-cfg__cat-select-wrap:not([hidden]) .outfit-cfg__cat-select');
    var currentCollection = currentSelect ? currentSelect.value : '';
    var anyFilter = activeFilters.length || activeSizes.length || activeLengths.length;

    cards.forEach(function(card) {
      var handle = card.dataset.handle;
      var product = productCache[handle];
      if (!product) { card.hidden = false; return; }
      var code = card.dataset.colorCode || null;
      var variants = (product.variants || []).filter(function(v) { return !code || extractColorCode(v.option1 || '') === code; });

      // r112: colour family (per-colour card → its own colour), Größe, Länge — all AND-combined
      var show = true;
      var matchingCode = null;
      if (activeFilters.length) {
        var fam = false;
        variants.forEach(function(v) {
          if (fam) return;
          var family = colorFamilyOf(v.option1);
          if (family && activeFilters.indexOf(family) > -1) { fam = true; matchingCode = extractColorCode(v.option1); }
        });
        if (!fam) show = false;
      }
      if (show && activeSizes.length) {
        var sz = false;
        variants.forEach(function(v) {
          if (sz || v.available === false) return;
          var sv = sizeOfVariant(product, v);
          if (sv && activeSizes.indexOf(sv) > -1) sz = true;
        });
        if (!sz) show = false;
      }
      if (show && activeLengths.length) {
        var len = card.dataset.laenge || normLen(getDirndlLength(product)) || (window.__goesslLenCache ? normLen(window.__goesslLenCache[handle]) : '');
        if (activeLengths.indexOf(len) === -1) show = false;
      }
      if (show && !isLengthCompatible(product, currentCollection)) show = false;
      card.hidden = !show;
      if (!show) return;

      if (code) return; // per-colour card keeps its own photo
      if (!activeFilters.length) { resetCardImage(card); return; }
      // Legacy single-card products: swap the photo + swatch to the matching colour
      if (matchingCode) {
        var colorImg = findImageForColor(product, matchingCode);
        var cardImg = card.querySelector('.goessl-product-card__img');
        if (colorImg && cardImg) cardImg.src = colorImg.src.replace(/\.([^.]+)$/, '_300x.$1');
        card.querySelectorAll('.goessl-product-card__swatch').forEach(function(sw) {
          sw.classList.toggle('active', sw.dataset.colorCode === matchingCode);
        });
        selectedColors[handle] = matchingCode;
      }
    });
    void anyFilter;
  }

  // Reset card image to its original (first) image
  function resetCardImage(card) {
    if (card.dataset.colorCode) return; // r112: per-colour cards keep their own photo
    var handle = card.dataset.handle;
    var product = productCache[handle];
    if (!product || !product.images || !product.images[0]) return;
    var cardImg = card.querySelector('.goessl-product-card__img');
    if (cardImg) {
      cardImg.src = product.images[0].src.replace(/\.([^.]+)$/, '_300x.$1');
    }
    // Reset swatch to first
    var swatches = card.querySelectorAll('.goessl-product-card__swatch');
    swatches.forEach(function(s, i) {
      s.classList.toggle('is-active', i === 0);
    });
    delete selectedColors[handle];
  }


  // Track selected color per product handle
  var selectedColors = {};

  // r112: the colours a product can show on a card — unique FarbNr with a photo (same rule the swatch row used)
  function colourPicksFor(product) {
    var picks = [], seen = {};
    (product.variants || []).forEach(function(v) {
      var farbe = v.option1 || '';
      var code = extractColorCode(farbe);
      if (!code || seen[code]) return;
      seen[code] = true;
      var img = findImageForColor(product, code);
      if (!img) return;
      picks.push({ code: code, name: farbe.replace(/\s\d+$/, '').trim(), image: img, variantId: v.id });
    });
    return picks;
  }
  // r112: the size value of a variant (the option named Größe/Size; falls back to the last option)
  function sizeIndexOf(product) {
    var opts = product.options || [];
    var idx = -1;
    opts.forEach(function(o, i) { var n = String(o && o.name != null ? o.name : o); if (/gr(ö|oe|o)(ß|ss)e|size/i.test(n)) idx = i; });
    if (idx === -1 && opts.length > 1) idx = opts.length - 1;
    return idx;
  }
  function optionValueAt(v, idx) { return idx === 0 ? v.option1 : idx === 1 ? v.option2 : idx === 2 ? v.option3 : null; }
  function sizeOfVariant(product, v) {
    var val = optionValueAt(v, sizeIndexOf(product));
    return val ? String(val).replace(/\s*\(.*\)$/, '').trim() : null;
  }
  // r191: the size as the shop stores it ("48 (LEDERHOSEN)") - the personal size memory matches on this
  function rawSizeOfVariant(product, v) {
    var val = optionValueAt(v, sizeIndexOf(product));
    return val ? String(val).trim() : null;
  }
  // r191: Lederhosen and men's jackets carry a third option between Farbe and Größe (Schnitt: Hosengröße,
  // Kragenweite …). Everything that read `option2 || option1` as the size listed the Schnitt instead of 44 … 60.
  function cutOfVariant(product, v) {
    var n = (product.options || []).length;
    if (n < 3) return '';
    var sizeIdx = sizeIndexOf(product);
    var cutIdx = sizeIdx === 1 ? 2 : 1;
    return String(optionValueAt(v, cutIdx) || '');
  }
  function variantKey(product, v) { return cutOfVariant(product, v) + '|' + (sizeOfVariant(product, v) || ''); }

  // r191: the sizes of ONE colour, one entry per Schnitt + size, in size order - the panel dropdown, the size
  // modal and the guided last step all list sizes from here (the r71 rule: never across colourways).
  function sizeEntriesFor(product, colorCode) {
    var seen = {}, out = [], cutOrder = [];
    (product.variants || []).forEach(function(v) {
      if (colorCode && extractColorCode(v.option1 || '') !== colorCode) return;
      var key = variantKey(product, v);
      if (seen[key]) return;
      seen[key] = true;
      var cut = cutOfVariant(product, v);
      if (cutOrder.indexOf(cut) === -1) cutOrder.push(cut);
      out.push({ key: key, size: sizeOfVariant(product, v) || '', raw: rawSizeOfVariant(product, v) || '', cut: cut, variant: v });
    });
    var multiCut = cutOrder.length > 1;
    out.forEach(function(e) { e.label = multiCut && e.cut ? e.size + ' · ' + e.cut : e.size; });
    out.sort(function(a, b) {
      var c = cutOrder.indexOf(a.cut) - cutOrder.indexOf(b.cut);
      return c || (sizeSortKey(a.size) - sizeSortKey(b.size));
    });
    return out;
  }

  // r191: one colour switch for the card swatch, the per-colour card and the panel dots (three copies before):
  // keep the chosen Schnitt + size on the new colour when it exists there; a size the customer chose stays
  // chosen only when it really came along.
  function remapColour(product, it, code) {
    var prevKey = null;
    (product.variants || []).forEach(function(v) { if (v.id === it.variant_id) prevKey = variantKey(product, v); });
    it.colorCode = code;
    it.variant_id = null;
    (product.variants || []).forEach(function(v) {
      if (it.variant_id || extractColorCode(v.option1 || '') !== code || v.available === false) return;
      if (!prevKey || variantKey(product, v) === prevKey) it.variant_id = v.id;
    });
    if (!prevKey || !it.variant_id) { it.sizeSet = false; delete it.sizeFrom; }
  }

  // /collections/*/products.json and the .js endpoints give tags as an array, /products/{handle}.json as ONE
  // comma-separated string - code that indexed the string read single characters.
  function tagList(product) {
    var t = product && product.tags;
    if (Array.isArray(t)) return t;
    return String(t || '').split(',').map(function(s) { return s.trim(); }).filter(Boolean);
  }
  function normLen(v) { var m = String(v == null ? '' : v).match(/\d+/); return m ? m[0] : ''; }
  // r196: an apron's Länge is the length of the Dirndl it is made for, and its line says so ("Für Dirndllänge 70 cm") -
  // the tag rule of sections/main-product.liquid and the cart: a piece tagged Dirndl keeps its own Länge.
  function isApron(product) {
    var tags = tagList(product);
    return tags.indexOf('Frauen Schürzen') !== -1 && tags.indexOf('Frauen Dirndl') === -1 && tags.indexOf('Kinder Dirndl') === -1;
  }
  // r191: plain numbers sort as numbers (SIZE_ORDER lacks 43/45, which landed after 60 in shirt and Gilet
  // lists); letters and kids' sizes keep the SIZE_ORDER rank; '' (one size) first.
  function sizeSortKey(sz) {
    sz = String(sz == null ? '' : sz);
    if (!sz) return 0;
    if (/^\d+(\.\d+)?$/.test(sz)) return 1000 + parseFloat(sz);
    var i = SIZE_ORDER.indexOf(sz);
    return i > -1 ? i : 5000 + sz.charCodeAt(0);
  }
  // r112: switch the colour of a piece already on the canvas (was the swatch-click body)
  function switchStateColor(product, code) {
    var state = getState();
    for (var i = 0; i < state.length; i++) {
      if (state[i].handle !== product.handle) continue;
      remapColour(product, state[i], code);
      renderCanvas();
      renderSelectedPanel();
      updateCartButton();
      break;
    }
  }

  // Card builder \u2014 full parity with the collection page's `goessl-product-card`:
  // badges (Neu/Sale/Edition), length badge, hover-cycle image dots, quick-view +
  // wishlist hover buttons, image-swatch row, title, price (with "Ab" prefix when
  // variants vary). Card-body click adds to canvas; quick-view, wishlist and
  // swatches keep their own delegated handlers and are excluded from the card click.
  function createMiniCard(product, collection, pick) {
    var card = document.createElement('article');
    card.className = 'goessl-product-card outfit-cfg__product-card';
    card.dataset.handle = product.handle;
    if (pick) { card.dataset.colorCode = pick.code; card.dataset.colorName = pick.name; }
    // r112: per-colour card → only that colour's variants count (availability, sale, price, size filter)
    var cardVariants = pick
      ? (product.variants || []).filter(function(v) { return extractColorCode(v.option1 || '') === pick.code; })
      : (product.variants || []);
    var tagLen = getDirndlLength(product);
    if (tagLen) card.dataset.laenge = normLen(tagLen);

    // Hover-cycle image list (uses delegated handlers in card-product.liquid) — per colour when picked
    var hoverSource = pick ? imagesForColor(product, pick.code, pick.image) : (product.images || []);
    var hoverImages = [];
    hoverSource.slice(0, 6).forEach(function(im) {
      hoverImages.push(im.src.replace(/\.([^.]+)$/, '_500x.$1'));
    });
    if (hoverImages.length > 1) card.dataset.cardImages = hoverImages.join('|');

    // Badge detection
    var tagsLc = (product.tags || []).map(function(t) { return String(t).toLowerCase(); });
    // r69 (SJ): same NEU language as the collection cards — literal Neu,
    // current season 262, or the Kepka partner range
    var isNew = tagsLc.indexOf('neu') !== -1 || tagsLc.indexOf('262') !== -1 || product.vendor === 'Kepka';
    var isEdition = tagsLc.indexOf('edition') !== -1;
    // available may be null in /products/{handle}.json — treat null/undefined as available
    var isAvailable = cardVariants.some(function(v) { return v.available !== false; });
    var onSale = cardVariants.some(function(v) {
      return v.compare_at_price && parseFloat(v.compare_at_price) > parseFloat(v.price);
    });

    // \u2500\u2500 Image area \u2500\u2500
    var imgWrap = document.createElement('div');
    imgWrap.className = 'goessl-product-card__image';
    imgWrap.style.aspectRatio = '3 / 4';

    var frontImg = (pick && pick.image) ? pick.image : findFrontImage(product);
    if (frontImg) {
      var img = document.createElement('img');
      img.className = 'goessl-product-card__img';
      img.src = frontImg.src.replace(/\.([^.]+)$/, '_500x.$1');
      img.alt = product.title || '';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.dataset.baseSrc = img.src;
      imgWrap.appendChild(img);
    }

    // Hover-cycle dots
    if (hoverImages.length > 1) {
      var dots = document.createElement('div');
      dots.className = 'goessl-product-card__dots';
      dots.setAttribute('data-card-dots', '');
      hoverImages.forEach(function(_, i) {
        var d = document.createElement('span');
        d.className = 'goessl-product-card__dot' + (i === 0 ? ' is-active' : '');
        dots.appendChild(d);
      });
      imgWrap.appendChild(dots);
    }

    // Badges (Neu / Sale / Edition / Sold out)
    var badges = document.createElement('div');
    badges.className = 'goessl-product-card__badges';
    function addBadge(modifier, text) {
      var b = document.createElement('span');
      b.className = 'goessl-product-card__badge goessl-product-card__badge--' + modifier;
      b.textContent = text;
      badges.appendChild(b);
    }
    if (isEdition && isAvailable) addBadge('editions', window.g26t('products.card.edition_badge', 'Edition'));
    if (isNew && isAvailable) addBadge('new', window.g26t('products.product.new', 'Neu'));
    if (onSale) addBadge('sale', window.g26t('products.product.price.sale', 'Sale'));
    if (!isAvailable) addBadge('sold-out', window.g26t('products.product.sold_out', 'Ausverkauft'));
    if (badges.children.length) imgWrap.appendChild(badges);

    // Length badge (top-right): tag first, metafield fallback — ALL Dirndln,
    // Schürzen, Kleider and Röcke carry their Länge on the card (SJ 30.08).
    var length = getDirndlLength(product);
    if (length) {
      var lengthEl = document.createElement('span');
      lengthEl.className = 'goessl-product-card__length';
      lengthEl.textContent = length + 'cm';
      imgWrap.appendChild(lengthEl);
    } else if (/(dirndl|schuerzen|kleider|roecke)/.test(String(collection || ''))) {
      fetchLaenge(product.handle).then(function (v) {
        if (!v) return;
        card.dataset.laenge = normLen(v);
        var el = document.createElement('span');
        el.className = 'goessl-product-card__length';
        el.textContent = v;
        imgWrap.appendChild(el);
      });
    }

    // Quick-view button (delegated handler in quick-view.js opens the modal)
    var qv = document.createElement('button');
    qv.type = 'button';
    qv.className = 'goessl-product-card__quick-view';
    qv.setAttribute('data-quick-view-trigger', product.handle);
    qv.setAttribute('aria-label', window.g26tText('products.card.quick_view', 'Schnellansicht'));
    qv.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>';
    imgWrap.appendChild(qv);

    // Wishlist toggle (handled by wishlist module via delegation)
    var wl = document.createElement('button');
    wl.type = 'button';
    wl.className = 'goessl-product-card__wishlist';
    wl.setAttribute('data-wishlist-toggle', '');
    wl.setAttribute('data-product-handle', product.handle);
    wl.setAttribute('aria-label', window.g26tText('products.product.wishlist_add', 'Zur Merkliste hinzufügen'));
    wl.innerHTML = '<svg class="wishlist-heart wishlist-heart--outline" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M10 17.5s-7-4.5-7-9.5a4 4 0 0 1 7-2.65A4 4 0 0 1 17 8c0 5-7 9.5-7 9.5z" stroke-linecap="round" stroke-linejoin="round"/></svg><svg class="wishlist-heart wishlist-heart--filled" width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path d="M10 17.5s-7-4.5-7-9.5a4 4 0 0 1 7-2.65A4 4 0 0 1 17 8c0 5-7 9.5-7 9.5z"/></svg>';
    imgWrap.appendChild(wl);

    card.appendChild(imgWrap);

    // \u2500\u2500 Extract unique colours \u2500\u2500
    var colors = [];
    var seenColors = {};
    (product.variants || []).forEach(function(v) {
      var farbe = v.option1 || '';
      var code = extractColorCode(farbe);
      if (!code || seenColors[code]) return;
      seenColors[code] = true;
      var name = farbe.replace(/\s\d+$/, '').trim();
      var colorImg = findImageForColor(product, code);
      // Don't surface colors that have no photo — they'd render as a solid color
      // swatch, which looks broken. Show only colors with an image; the product
      // itself stays visible as long as ≥1 colour has photos. (SJ 2026-05-22)
      if (!colorImg) return;
      colors.push({ code: code, name: name, image: colorImg, variantId: v.id });
    });

    // r112: a per-colour card carries exactly its own colour — no swatch row
    if (pick) colors = [pick];

    // Image-swatch row (between image and content, like collection cards)
    if (colors.length > 1) {
      // r69 (SJ): the photo must match the pre-selected FIRST swatch — the
      // featured image can be a different colourway (red photo under an
      // olive-active swatch made the card add a colour nobody chose).
      var cardImg0 = card.querySelector('.goessl-product-card__img');
      if (cardImg0 && colors[0].image) {
        cardImg0.src = colors[0].image.src.replace(/\.([^.]+)$/, '_500x.$1');
        cardImg0.dataset.baseSrc = cardImg0.src;
      }
      var swatchesWrap = document.createElement('div');
      swatchesWrap.className = 'goessl-product-card__swatches-wrap';

      var swatchRow = document.createElement('div');
      swatchRow.className = 'goessl-product-card__swatches';

      colors.forEach(function(color, idx) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'goessl-product-card__swatch' + (idx === 0 ? ' active' : '');
        dot.setAttribute('aria-label', window.g26tText('products.product.color_option', 'Farbe: {{ color }}', { color: color.name }));
        dot.dataset.colorCode = color.code;
        if (color.image) {
          dot.dataset.imageSrc = color.image.src.replace(/\.([^.]+)$/, '_500x.$1');
          var swatchImg = document.createElement('img');
          swatchImg.src = color.image.src.replace(/\.([^.]+)$/, '_80x80_crop_center.$1');
          swatchImg.alt = color.name;
          swatchImg.loading = 'lazy';
          swatchImg.width = 34;
          swatchImg.height = 34;
          dot.appendChild(swatchImg);
        } else {
          dot.style.backgroundColor = getColorHex(color.code);
        }

        dot.addEventListener('click', function(e) {
          e.stopPropagation();
          swatchRow.querySelectorAll('.goessl-product-card__swatch').forEach(function(d) {
            d.classList.remove('active');
          });
          dot.classList.add('active');
          selectedColors[product.handle] = color.code;

          if (color.image) {
            var cardImg = card.querySelector('.goessl-product-card__img');
            if (cardImg) {
              cardImg.src = color.image.src.replace(/\.([^.]+)$/, '_500x.$1');
              cardImg.dataset.baseSrc = cardImg.src;
            }
            // Reset hover-cycle for new colour
            card.removeAttribute('data-card-images');
            var dotsWrap = card.querySelector('[data-card-dots]');
            if (dotsWrap) dotsWrap.style.display = 'none';
          }

          var state = getState();
          for (var i = 0; i < state.length; i++) {
            if (state[i].handle === product.handle) {
              // Same remap the Auswahl panel dots do: keep the chosen size
              // across the colour switch and never leave a variant of the OLD
              // colour behind — panel thumb/swatches + cart follow the canvas
              // (SJ 30.08: canvas navy, panel lilac on the Schürze).
              remapColour(product, state[i], color.code);
              renderCanvas();
              renderSelectedPanel();
              updateCartButton();
              break;
            }
          }
        });

        swatchRow.appendChild(dot);
      });

      swatchesWrap.appendChild(swatchRow);

      var arrow = document.createElement('button');
      arrow.type = 'button';
      arrow.className = 'goessl-product-card__swatches-arrow';
      arrow.setAttribute('aria-label', window.g26tText('products.card.more_colors', 'Mehr Farben'));
      arrow.innerHTML = '<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M4.5 2.5L8 6L4.5 9.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      swatchesWrap.appendChild(arrow);

      card.appendChild(swatchesWrap);
    }

    // \u2500\u2500 Content (title + price) \u2500\u2500
    var content = document.createElement('div');
    content.className = 'goessl-product-card__content';

    var title = document.createElement('h3');
    title.className = 'goessl-product-card__title';
    title.textContent = product.title || '';
    content.appendChild(title);
    if (pick && pick.name) {
      var colourLine = document.createElement('div');
      colourLine.className = 'goessl-product-card__colour';
      colourLine.textContent = pick.name;
      content.appendChild(colourLine);
    }

    if (cardVariants.length) {
      var price = document.createElement('div');
      price.className = 'goessl-product-card__price';
      var prices = cardVariants.map(function(v) { return parseFloat(v.price); });
      var minP = Math.min.apply(null, prices);
      var maxP = Math.max.apply(null, prices);
      var formatted = money(minP);
      var prefix = (maxP > minP) ? window.g26t('products.product.price.from', 'Ab') + ' ' : '';
      price.innerHTML = '<span class="goessl-product-card__price-regular">' + prefix + formatted + '</span>';
      content.appendChild(price);
    }

    card.appendChild(content);

    // Card-body activation adds to canvas. Skip when target is a swatch /
    // quick-view / wishlist / arrow (those have their own handlers).
    function activateCard(e) {
      if (e.target.closest('.goessl-product-card__swatch')) return false;
      if (e.target.closest('[data-quick-view-trigger]')) return false;
      if (e.target.closest('[data-wishlist-toggle]')) return false;
      if (e.target.closest('.goessl-product-card__swatches-arrow')) return false;
      // Default to the card's active swatch (first renderable colour). A null
      // colorCode makes canvas fall back to images[0] while card/panel default
      // to variant-order colors[0] — three renderers, two answers (SJ 30.08).
      var colorCode = pick ? pick.code : (selectedColors[product.handle] || (colors.length ? colors[0].code : null));
      if (pick) {
        // r112: the piece is on the canvas in another colour → switch the colour instead of removing it
        var st = getState();
        for (var si = 0; si < st.length; si++) {
          if (st[si].handle === product.handle && st[si].colorCode !== pick.code) {
            saveUndo();
            switchStateColor(product, pick.code);
            highlightSelectedCards();
            return true;
          }
        }
      }
      selectProduct(product, collection, colorCode);
      highlightSelectedCards();
      return true;
    }

    // !! DO NOT REMOVE !! (iOS two-tap on :hover)
    // iOS Safari treats the first tap on an element with :hover rules as
    // "show hover state" and only fires `click` on the second tap. The
    // .goessl-product-card has multiple :hover rules (image swap, plus
    // icon, etc.) — so on iPhone it took two taps to add a product.
    // Fix: handle touchend ourselves and call preventDefault() to stop
    // the synthetic click. Track touchmove to skip scroll gestures.
    var touchMoved = false;
    card.addEventListener('touchstart', function() { touchMoved = false; }, { passive: true });
    card.addEventListener('touchmove', function() { touchMoved = true; }, { passive: true });
    card.addEventListener('touchend', function(e) {
      if (touchMoved) return; // it was a scroll, not a tap
      if (activateCard(e)) e.preventDefault(); // suppress the synthetic click
    });

    card.addEventListener('click', activateCard);

    return card;
  }

  // ═══════════════════════════════════════════
  //  CANVAS MANAGEMENT — dynamic layout, no fixed slots
  // ═══════════════════════════════════════════

  function selectProduct(product, collection, colorCode) {
    var state = getState();

    // Toggle: if already on canvas, remove it
    for (var i = 0; i < state.length; i++) {
      if (state[i].handle === product.handle) {
        saveUndo();
        state.splice(i, 1);
        renderCanvas();
        updateUI();
        applyFilters();
        return;
      }
    }

    // r191: the guided path keeps one piece per step - a new pick takes the place of the step's piece
    var replaceIdx = guideReplaceIndex(collection);
    if (replaceIdx === -1 && state.length >= MAX_ITEMS) {
      showNotice(
        window.g26t('configurator.notice_full_title', 'Canvas voll'),
        window.g26t('configurator.notice_full_message', 'Sie haben bereits {{ count }} Stücke auf dem Canvas. Bitte entfernen Sie eines, um ein anderes hinzuzufügen.', { count: MAX_ITEMS })
      );
      return;
    }
    saveUndo();

    // Pick first available variant (matching color if specified)
    var variantId = null;
    if (product.variants) {
      for (var v = 0; v < product.variants.length; v++) {
        var variant = product.variants[v];
        if (colorCode) {
          var vColor = extractColorCode(variant.option1);
          if (vColor !== colorCode) continue;
        }
        // available may be null — only skip if explicitly false
        if (variant.available !== false) { variantId = variant.id; break; }
      }
      if (!variantId && product.variants[0]) variantId = product.variants[0].id;
    }

    var newItem = {
      handle: product.handle,
      collection: collection,
      variant_id: variantId,
      sizeScale: getCategorySize(collection),
      colorCode: colorCode || null
    };
    if (replaceIdx > -1) {
      // same spot on the canvas: a dragged or resized Dirndl is swapped where it stands
      var old = state[replaceIdx];
      ['customX', 'customY', 'customW', 'customH'].forEach(function(k) { if (old[k] != null) newItem[k] = old[k]; });
      state.splice(replaceIdx, 1, newItem);
    } else {
      state.push(newItem);
    }

    renderCanvas();
    updateUI();
    applyFilters(); // Re-filter grid for length compatibility
  }

  function removeItem(idx) {
    saveUndo();
    var state = getState();
    state.splice(idx, 1);
    renderCanvas();
    updateUI();
    applyFilters(); // Re-filter grid for length compatibility
  }

  function renderCanvas() {
    canvasItems.replaceChildren();
    var state = getState();

    if (state.length === 0) {
      if (canvasEmpty) canvasEmpty.hidden = false;
      return;
    }
    if (canvasEmpty) canvasEmpty.hidden = true;

    // Sort items: largest category size first for best grid assignment
    var sorted = state.map(function(item, idx) {
      return { item: item, origIdx: idx };
    }).sort(function(a, b) {
      return (b.item.sizeScale || 0.7) - (a.item.sizeScale || 0.7);
    });

    // Pick grid layout for this item count
    var count = Math.min(sorted.length, 8);
    var grid = GRID_LAYOUTS[count] || GRID_LAYOUTS[8];

    sorted.forEach(function(entry, gridIdx) {
      var item = entry.item;
      var product = productCache[item.handle];
      if (!product) return;

      var cell = grid[gridIdx];
      if (!cell) return;

      // Scale within cell based on category size
      var scale = item.sizeScale || 0.7;
      var cellW = cell.w * scale;
      var cellH = cell.h * scale;
      var cellX = cell.x + (cell.w - cellW) / 2;
      var cellY = cell.y + (cell.h - cellH) / 2;

      // Use custom position if dragged
      var posX = item.customX != null ? item.customX : cellX;
      var posY = item.customY != null ? item.customY : cellY;

      var el = document.createElement('div');
      el.className = 'outfit-cfg__canvas-item';
      el.style.left = (posX * 100) + '%';
      el.style.top = (posY * 100) + '%';
      // Honor custom dimensions if user previously resized
      var displayW = item.customW != null ? item.customW : cellW;
      var displayH = item.customH != null ? item.customH : cellH;
      el.style.width = (displayW * 100) + '%';
      el.style.height = (displayH * 100) + '%';
      el.style.zIndex = gridIdx + 2;
      el.style.cursor = 'grab';

      // Store cell info for drag bounds
      var slotInfo = { w: displayW, h: displayH, z: gridIdx + 2 };
      makeDraggable(el, item, slotInfo);
      makeResizable(el, item, slotInfo);

      // Use color-specific image if available
      var displayImage = null;
      if (item.colorCode) {
        displayImage = findImageForColor(product, item.colorCode);
      }
      if (!displayImage && product.images && product.images[0]) {
        displayImage = product.images[0];
      }

      if (displayImage) {
        var img = document.createElement('img');
        img.src = displayImage.src.replace(/\.([^.]+)$/, '_800x.$1');
        img.alt = product.title || '';
        el.appendChild(img);
      }

      // Tooltip with name + price
      var price = product.variants && product.variants[0] ? product.variants[0].price : null;
      var tooltip = document.createElement('div');
      tooltip.className = 'outfit-cfg__canvas-tooltip';
      tooltip.textContent = product.title + (price ? ' \u2014 ' + money(price) : '');
      el.appendChild(tooltip);

      // Remove button
      var removeBtn = document.createElement('button');
      removeBtn.className = 'outfit-cfg__canvas-item__remove';
      removeBtn.textContent = '\u00D7';
      var removeIdx = entry.origIdx;
      removeBtn.addEventListener('click', function(e) {
        e.stopPropagation();
        removeItem(removeIdx);
      });
      el.appendChild(removeBtn);

      canvasItems.appendChild(el);
    });
  }

  // ═══════════════════════════════════════════
  //  RESIZE (4 corner handles)
  // ═══════════════════════════════════════════

  function makeResizable(el, stateItem, slot) {
    var corners = ['nw', 'ne', 'sw', 'se'];
    var MIN_SIZE = 0.05; // 5% of canvas

    corners.forEach(function(corner) {
      var handle = document.createElement('div');
      handle.className = 'outfit-cfg__canvas-item__resize outfit-cfg__canvas-item__resize--' + corner;
      handle.setAttribute('aria-label', window.g26tText('configurator.resize', 'Größe ändern'));
      el.appendChild(handle);

      var startX, startY, startLeft, startTop, startW, startH, resizing = false;
      var containerRect;

      handle.addEventListener('pointerdown', function(e) {
        e.preventDefault();
        e.stopPropagation();
        resizing = true;
        handle.setPointerCapture(e.pointerId);
        containerRect = canvasItems.getBoundingClientRect();
        startX = e.clientX;
        startY = e.clientY;
        startLeft = parseFloat(el.style.left) / 100;
        startTop = parseFloat(el.style.top) / 100;
        startW = parseFloat(el.style.width) / 100;
        startH = parseFloat(el.style.height) / 100;
        el.style.zIndex = 30;
        el.style.transition = 'none';
        el.classList.add('is-resizing');
      });

      handle.addEventListener('pointermove', function(e) {
        if (!resizing) return;
        var dx = (e.clientX - startX) / containerRect.width;
        var dy = (e.clientY - startY) / containerRect.height;
        var newLeft = startLeft, newTop = startTop, newW = startW, newH = startH;

        if (corner === 'se') {
          newW = Math.max(MIN_SIZE, Math.min(1 - newLeft, startW + dx));
          newH = Math.max(MIN_SIZE, Math.min(1 - newTop, startH + dy));
        } else if (corner === 'sw') {
          newLeft = Math.max(0, Math.min(startLeft + startW - MIN_SIZE, startLeft + dx));
          newW = startW + (startLeft - newLeft);
          newH = Math.max(MIN_SIZE, Math.min(1 - newTop, startH + dy));
        } else if (corner === 'ne') {
          newTop = Math.max(0, Math.min(startTop + startH - MIN_SIZE, startTop + dy));
          newH = startH + (startTop - newTop);
          newW = Math.max(MIN_SIZE, Math.min(1 - newLeft, startW + dx));
        } else if (corner === 'nw') {
          newLeft = Math.max(0, Math.min(startLeft + startW - MIN_SIZE, startLeft + dx));
          newW = startW + (startLeft - newLeft);
          newTop = Math.max(0, Math.min(startTop + startH - MIN_SIZE, startTop + dy));
          newH = startH + (startTop - newTop);
        }

        el.style.left = (newLeft * 100) + '%';
        el.style.top = (newTop * 100) + '%';
        el.style.width = (newW * 100) + '%';
        el.style.height = (newH * 100) + '%';
      });

      handle.addEventListener('pointerup', function(e) {
        if (!resizing) return;
        resizing = false;
        el.classList.remove('is-resizing');
        el.style.zIndex = slot.z || stateItem.priority;
        el.style.transition = '';

        // Persist custom dims + position
        stateItem.customX = parseFloat(el.style.left) / 100;
        stateItem.customY = parseFloat(el.style.top) / 100;
        stateItem.customW = parseFloat(el.style.width) / 100;
        stateItem.customH = parseFloat(el.style.height) / 100;
        // Update slot bounds for subsequent drags
        slot.w = stateItem.customW;
        slot.h = stateItem.customH;

        if (typeof updateCanvasButtons === 'function') updateCanvasButtons();
        // Refresh QR so it encodes the new layout
        updateCanvasQR();
      });
    });
  }

  // ═══════════════════════════════════════════
  //  DRAG TO REPOSITION
  // ═══════════════════════════════════════════

  function makeDraggable(el, stateItem, slot) {
    var startX, startY, startLeft, startTop, dragging = false;
    var didMove = false;
    var containerRect;

    el.addEventListener('pointerdown', function(e) {
      if (e.target.closest('.outfit-cfg__canvas-item__remove')) return;
      e.preventDefault();
      dragging = true;
      didMove = false;
      el.style.cursor = 'grabbing';
      el.style.zIndex = 20;
      el.style.transition = 'none';
      el.setPointerCapture(e.pointerId);

      containerRect = canvasItems.getBoundingClientRect();
      startX = e.clientX;
      startY = e.clientY;
      startLeft = parseFloat(el.style.left) / 100;
      startTop = parseFloat(el.style.top) / 100;
    });

    el.addEventListener('pointermove', function(e) {
      if (!dragging) return;
      var dx = (e.clientX - startX) / containerRect.width;
      var dy = (e.clientY - startY) / containerRect.height;
      if (Math.abs(dx) > 0.01 || Math.abs(dy) > 0.01) didMove = true;
      var newX = Math.max(0, Math.min(1 - slot.w, startLeft + dx));
      var newY = Math.max(0, Math.min(1 - slot.h, startTop + dy));
      el.style.left = (newX * 100) + '%';
      el.style.top = (newY * 100) + '%';
    });

    el.addEventListener('pointerup', function(e) {
      if (!dragging) return;
      dragging = false;
      el.style.cursor = 'grab';
      el.style.zIndex = slot.z || stateItem.priority;
      el.style.transition = '';

      // Save custom position
      var finalX = parseFloat(el.style.left) / 100;
      var finalY = parseFloat(el.style.top) / 100;
      stateItem.customX = finalX;
      stateItem.customY = finalY;
      updateCanvasButtons();
      // Refresh QR so it encodes the new layout
      if (didMove) updateCanvasQR();

      if (!didMove) {
        // Tap without drag — toggle tapped state (mobile remove + tooltip)
        var wasTapped = el.classList.contains('is-tapped');
        canvasItems.querySelectorAll('.outfit-cfg__canvas-item').forEach(function(c) {
          c.classList.remove('is-tapped');
        });
        if (!wasTapped) el.classList.add('is-tapped');
        // r191: in the guided path the tap also opens the piece's step, where it can be swapped
        if (guide.active) guideOpenStepOf(stateItem);
      }
    });
  }

  // Drag for canvas overlays (logo, QR) — simpler, pixel-based
  function makeOverlayDraggable(el) {
    if (!el) return;
    var startX, startY, origLeft, origTop, dragging = false;
    var canvas;

    el.addEventListener('pointerdown', function(e) {
      e.preventDefault();
      dragging = true;
      el.style.cursor = 'grabbing';
      el.style.transition = 'none';
      el.setPointerCapture(e.pointerId);

      canvas = el.closest('.outfit-cfg__canvas');
      var rect = canvas.getBoundingClientRect();
      var elRect = el.getBoundingClientRect();
      startX = e.clientX;
      startY = e.clientY;
      origLeft = elRect.left - rect.left;
      origTop = elRect.top - rect.top;
      // Switch to absolute positioning
      el.style.left = origLeft + 'px';
      el.style.top = origTop + 'px';
      el.style.bottom = 'auto';
      el.style.right = 'auto';
      el.style.transform = 'none';
    });

    el.addEventListener('pointermove', function(e) {
      if (!dragging) return;
      var dx = e.clientX - startX;
      var dy = e.clientY - startY;
      el.style.left = (origLeft + dx) + 'px';
      el.style.top = (origTop + dy) + 'px';
    });

    el.addEventListener('pointerup', function() {
      if (!dragging) return;
      dragging = false;
      el.style.cursor = 'grab';
      el.style.transition = '';
      // Convert px → normalized 0-1 of the canvas so position survives sharing.
      var canvasRect = canvas.getBoundingClientRect();
      var elRect = el.getBoundingClientRect();
      var nx = (elRect.left - canvasRect.left) / canvasRect.width;
      var ny = (elRect.top - canvasRect.top) / canvasRect.height;
      el.style.left = (nx * 100) + '%';
      el.style.top = (ny * 100) + '%';
      el.dataset.dragged = 'true';
      el.dataset.normX = String(nx);
      el.dataset.normY = String(ny);
      updateCanvasQR();
    });
  }

  // Apply a stored normalized QR position from a shared URL.
  function applyQrLayout(spec) {
    var qrEl = document.querySelector('[data-canvas-qr]');
    if (!qrEl || !spec) return;
    var nx = spec.x != null ? spec.x / 1000 : null;
    var ny = spec.y != null ? spec.y / 1000 : null;
    if (nx == null || ny == null) return;
    qrEl.style.left = (nx * 100) + '%';
    qrEl.style.top = (ny * 100) + '%';
    qrEl.style.bottom = 'auto';
    qrEl.style.right = 'auto';
    qrEl.style.transform = 'none';
    qrEl.dataset.dragged = 'true';
    qrEl.dataset.normX = String(nx);
    qrEl.dataset.normY = String(ny);
  }

  // ═══════════════════════════════════════════
  //  UNDO
  // ═══════════════════════════════════════════

  function saveUndo() {
    undoStack.push(JSON.stringify(getState()));
    if (undoStack.length > MAX_UNDO) undoStack.shift();
    updateCanvasButtons();
  }

  function undo() {
    if (!undoStack.length) return;
    var prev = JSON.parse(undoStack.pop());
    setState(prev);
    renderCanvas();
    updateUI();
    applyFilters();
    updateCanvasButtons();
  }

  function resetPositions() {
    getState().forEach(function(item) {
      delete item.customX;
      delete item.customY;
    });
    renderCanvas();
    updateCanvasButtons();
  }

  function clearCanvas() {
    saveUndo();
    setState([]);
    guide.step = 0;
    guide.skipped = {};
    renderCanvas();
    updateUI();
    highlightSelectedCards();
    updateCanvasButtons();
  }

  function updateCanvasButtons() {
    var state = getState();
    var hasItems = state.length > 0;
    var hasCustom = state.some(function(item) {
      return item.customX != null || item.customY != null;
    });
    if (resetBtn) resetBtn.hidden = !hasCustom;
    if (clearBtn) clearBtn.hidden = !hasItems;
    if (undoBtn) undoBtn.hidden = !undoStack.length;
  }

  function updateUI() {
    var state = getState();
    var hasItems = state.length > 0;

    // Refresh QR code so it always points to the current outfit
    updateCanvasQR();

    // Sticky footer is replaced by the inline selected-items panel
    if (stickyFooter) stickyFooter.hidden = true;

    renderSelectedPanel();
    updateTotal();
    highlightSelectedCards();
    updateCanvasButtons();
    syncUrl(hasItems);
  }

  // Persist current outfit in the URL so a reload restores the canvas.
  // replaceState (not pushState) — we don't want every add/drag/resize
  // to add a back-button history entry.
  function syncUrl(hasItems) {
    try {
      if (hasItems) {
        var u = getShareUrl();
        if (u && u !== window.location.href) {
          history.replaceState(null, '', u);
        }
      } else if (window.location.search) {
        history.replaceState(null, '', window.location.pathname);
      }
    } catch (e) { /* ignore — non-critical */ }
  }

  // ═══════════════════════════════════════════
  //  SELECTED ITEMS PANEL (right of canvas)
  //  Inline size + colour + remove for each item; CTA pinned to bottom.
  // ═══════════════════════════════════════════

  function renderSelectedPanel() {
    renderGuide(); // r191: every state change that redraws the panel redraws the guided step too
    if (!selectedList) return;
    var state = getState();
    var hasItems = state.length > 0;

    if (selectedCount) selectedCount.textContent = String(state.length);

    selectedList.replaceChildren();

    if (!hasItems) {
      var empty = document.createElement('p');
      empty.className = 'outfit-cfg__selected-empty';
      empty.textContent = window.g26tText('configurator.selection_empty', 'Wählen Sie Stücke aus den Kategorien unten, um Ihr Outfit zusammenzustellen.');
      selectedList.appendChild(empty);
      if (selectedCart) selectedCart.disabled = true;
      if (selectedTotal) selectedTotal.textContent = money(0);
      return;
    }

    state.forEach(function(item, idx) {
      var product = productCache[item.handle];
      if (!product) return;

      var row = document.createElement('div');
      row.className = 'outfit-cfg__selected-row';
      row.dataset.rowIdx = String(idx);

      // Effective colour: an item without an explicit pick behaves as the FIRST
      // variant's colour everywhere (thumb, size list, dots). Before this fix the
      // thumb fell back to images[0] (often a different colourway) while the size
      // dedupe silently resolved to the first colour — the cart then got a colour
      // the customer never saw (SJ 26.08: türkis shown, kiesel added).
      if (!item.colorCode && product.variants && product.variants[0]) {
        var firstCode = extractColorCode(product.variants[0].option1 || '');
        if (firstCode) item.colorCode = firstCode;
      }

      // Thumbnail (uses colour-specific image when available)
      var thumb = document.createElement('div');
      thumb.className = 'outfit-cfg__selected-thumb';
      var img = null;
      if (item.colorCode) img = findImageForColor(product, item.colorCode);
      if (!img && product.images && product.images[0]) img = product.images[0];
      if (img) {
        var imgEl = document.createElement('img');
        imgEl.src = img.src.replace(/\.([^.]+)$/, '_200x.$1');
        imgEl.alt = product.title || '';
        imgEl.loading = 'lazy';
        thumb.appendChild(imgEl);
      }
      row.appendChild(thumb);

      // Info column: name, price, controls
      var info = document.createElement('div');
      info.className = 'outfit-cfg__selected-info';

      var name = document.createElement('p');
      name.className = 'outfit-cfg__selected-name';
      name.textContent = product.title || '';
      info.appendChild(name);

      if (product.variants && product.variants[0]) {
        var price = document.createElement('p');
        price.className = 'outfit-cfg__selected-price';
        price.textContent = money(product.variants[0].price);
        info.appendChild(price);
      }

      // Länge (Dirndl/Schürzen — SJ 26.08): metafield isn't in /products/{handle}.json,
      // so a tiny alternate template (product.laenge) serves it same-origin; cached per handle.
      var lenEl = document.createElement('p');
      lenEl.className = 'outfit-cfg__selected-length';
      lenEl.hidden = true;
      info.appendChild(lenEl);
      fetchLaenge(item.handle).then(function (v) {
        if (!v) return;
        lenEl.textContent = isApron(product)
          ? window.g26tText('cart.items.apron_length_line', 'Für Dirndllänge {{ length }}', { length: v })
          : window.g26t('cart.items.length_line', 'Länge {{ length }}', { length: v });
        lenEl.hidden = false;
      });

      var controls = document.createElement('div');
      controls.className = 'outfit-cfg__selected-controls';

      // Size dropdown (variant_id stored in state.variant_id)
      var sizeSelect = document.createElement('select');
      sizeSelect.className = 'outfit-cfg__selected-size';
      var placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = window.g26tText('products.product.choose_size', 'Größe wählen');
      placeholder.disabled = true;
      placeholder.selected = !item.variant_id;
      sizeSelect.appendChild(placeholder);

      // Size list of the current colorCode only (r71), one entry per Schnitt + size (r191)
      var sizeEntries = sizeEntriesFor(product, item.colorCode).filter(function(e) { return e.label; });

      sizeEntries.forEach(function(e) {
        var opt = document.createElement('option');
        opt.value = String(e.variant.id);
        // !! DO NOT REMOVE !! (false ausverkauft — gotcha #3)
        // Shopify /products/{handle}.json returns `available: null` for some products
        // (cache or sales-channel quirk) even when stock exists in Admin. Only mark
        // ausverkauft on an EXPLICIT false — null/undefined means "unknown", treat as
        // available. Cart-add still validates real stock at checkout.
        var isSoldOut = e.variant.available === false;
        opt.textContent = e.label + (isSoldOut ? ' — ' + window.g26t('configurator.sold_out_lower', 'ausverkauft') : '');
        if (isSoldOut) opt.disabled = true;
        if (item.variant_id === e.variant.id) opt.selected = true;
        sizeSelect.appendChild(opt);
      });

      sizeSelect.addEventListener('change', function() {
        var newId = parseInt(sizeSelect.value, 10);
        if (!isNaN(newId)) {
          item.variant_id = newId;
          markSizeChosen(item);
          updateCartButton();
          updateTotal();
          syncUrl(true);
        }
      });

      // one-size pieces (no size option) get no dropdown - it used to list the colour as the size
      if (sizeEntries.length) controls.appendChild(sizeSelect);

      // Colour dots — only when product has more than one colour
      var colorEntries = [];
      var seenColors = {};
      (product.variants || []).forEach(function(v) {
        var code = extractColorCode(v.option1 || '');
        if (!code || seenColors[code]) return;
        seenColors[code] = true;
        var ceImg = findImageForColor(product, code);
        // Skip colors with no photo so they don't render as solid swatches in the
        // canvas colour picker. Product stays usable on its imaged colours. (SJ 2026-05-22)
        if (!ceImg) return;
        colorEntries.push({ code: code, name: (v.option1 || '').replace(/\s\d+$/, '').trim(), image: ceImg });
      });

      if (colorEntries.length > 1) {
        var colorBar = document.createElement('div');
        colorBar.className = 'outfit-cfg__selected-colors';
        colorEntries.forEach(function(c) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'outfit-cfg__selected-color';
          dot.title = c.name;
          dot.setAttribute('aria-label', window.g26tText('products.product.color_option', 'Farbe: {{ color }}', { color: c.name }));
          var activeCode = item.colorCode || colorEntries[0].code;
          if (c.code === activeCode) dot.classList.add('is-active');
          if (c.image) {
            dot.style.backgroundImage = "url('" + c.image.src.replace(/\.([^.]+)$/, '_80x80_crop_center.$1') + "')";
            dot.style.backgroundSize = 'cover';
            dot.style.backgroundPosition = 'center';
          } else {
            dot.style.backgroundColor = getColorHex(c.code);
          }
          dot.addEventListener('click', function() {
            // Remap the chosen size onto the new colour instead of clearing it:
            // the customer keeps "46" across a colour switch, and the cart can
            // never silently fall back to another colourway.
            remapColour(product, item, c.code);
            renderCanvas();
            renderSelectedPanel();
            updateCartButton();
          });
          colorBar.appendChild(dot);
        });
        controls.appendChild(colorBar);
      }

      info.appendChild(controls);
      row.appendChild(info);

      // Remove button
      var removeBtn = document.createElement('button');
      removeBtn.className = 'outfit-cfg__selected-remove';
      removeBtn.type = 'button';
      removeBtn.setAttribute('aria-label', window.g26tText('cart.items.remove', 'Entfernen'));
      removeBtn.textContent = '×';
      removeBtn.addEventListener('click', function() { removeItem(idx); });
      row.appendChild(removeBtn);

      selectedList.appendChild(row);
    });

    updateCartButton();
  }

  function updateCartButton() {
    if (!selectedCart) return;
    var state = getState();
    if (!state.length) {
      selectedCart.disabled = true;
      selectedCart.textContent = window.g26tText('configurator.add_all_to_cart', 'Alle in den Warenkorb');
      return;
    }
    var missing = state.filter(function(it) { return !it.variant_id; }).length;
    selectedCart.disabled = false;
    selectedCart.textContent = missing > 0
      ? window.g26tText('configurator.choose_sizes_count', 'Größen wählen ({{ count }})', { count: missing })
      : window.g26t('configurator.add_all_to_cart', 'Alle in den Warenkorb');
  }

  // One cart line per piece. _Artikel-Nr comes from the product's "Artikelnr:" tag (gotcha #2 - the stores look
  // the Intex article up by it). r191: tags through tagList - pieces loaded from a share link, a saved outfit or
  // the PDP handoff come from /products/{handle}.json, where tags are ONE string, and the old character loop
  // never found the tag, so those lines reached the cart without the article number.
  function cartLineFor(stateItem, variantId) {
    var line = { id: variantId, quantity: 1 };
    var tags = tagList(productCache[stateItem.handle]);
    for (var ti = 0; ti < tags.length; ti++) {
      if (tags[ti].indexOf('Artikelnr:') === 0) {
        line.properties = { '_Artikel-Nr': tags[ti].slice(10).trim() };
        break;
      }
    }
    return line;
  }

  // The one add path for the panel button, the size modal and the guided last step: the pre-order plan per
  // size (r187), then the stock cap (r188), then the header count and the cart drawer.
  function postOutfitLines(cartItems, cartHandles) {
    return attachPreorderPlans(cartItems, cartHandles)
      .then(function() {
        return fetch(window.g26url('cart/add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: cartItems })
        });
      })
      .then(function(r) {
        return r.json().then(function(data) {
          if (r.ok && !data.status) return data;
          throw new Error(data.description || data.message || 'Cart error');
        });
      })
      .then(function() { return capPreorderLines(cartHandles); })
      .then(function() {
        fetch(window.g26url('cart.js')).then(function(r) { return r.json(); }).then(function(cart) {
          document.querySelectorAll('[data-cart-count]').forEach(function(el) {
            el.textContent = cart.item_count;
            el.hidden = !cart.item_count;
          });
        });
        document.dispatchEvent(new CustomEvent('cart:open'));
      });
  }

  function submitInlineCart() {
    if (!selectedCart) return;
    var state = getState();
    if (!state.length) return;
    var chosen = state.filter(function(it) { return it.variant_id; });
    var cartHandles = chosen.map(function(it) { return it.handle; });
    var cartItems = chosen.map(function(it) { return cartLineFor(it, it.variant_id); });
    if (!cartItems.length) return;

    var origLabel = selectedCart.textContent;
    selectedCart.textContent = window.g26tText('products.product.adding', 'Wird hinzugefügt...');
    selectedCart.disabled = true;

    postOutfitLines(cartItems, cartHandles)
      .then(function() {
        selectedCart.textContent = origLabel;
        selectedCart.disabled = false;
      })
      .catch(function(err) {
        console.error('Inline cart add failed:', err);
        selectedCart.textContent = window.g26tText('general.accessibility.error', 'Fehler');
        setTimeout(function() {
          selectedCart.textContent = origLabel;
          selectedCart.disabled = false;
        }, 2000);
      });
  }

  function outfitTotal() {
    var total = 0;
    getState().forEach(function(item) {
      var product = productCache[item.handle];
      if (!product || !product.variants || !product.variants.length) return;
      var matched = null;
      if (item.variant_id) {
        for (var i = 0; i < product.variants.length; i++) {
          if (product.variants[i].id === item.variant_id) { matched = product.variants[i]; break; }
        }
      }
      if (!matched) matched = product.variants[0]; // default-variant price as preview
      total += parseFloat(matched.price);
    });
    return total;
  }

  function updateTotal() {
    var formatted = money(outfitTotal());
    if (totalEl) totalEl.textContent = formatted;
    if (selectedTotal) selectedTotal.textContent = formatted;
  }

  // ═══════════════════════════════════════════
  //  BOTTOM BAR
  // ═══════════════════════════════════════════

  // ═══════════════════════════════════════════
  //  GENERIC NOTICE MODAL (empty canvas, max items, etc.)
  // ═══════════════════════════════════════════

  function showNotice(title, message) {
    var modal = document.querySelector('[data-notice-modal]');
    if (!modal) {
      // Fallback if markup not present
      window.alert(message);
      return;
    }
    var titleEl = modal.querySelector('[data-notice-modal-title]');
    var msgEl = modal.querySelector('[data-notice-modal-message]');
    if (titleEl) titleEl.textContent = title || '';
    if (msgEl) msgEl.textContent = message || '';
    modal.hidden = false;
    document.body.style.overflow = 'hidden';

    var closeFn = function() {
      modal.hidden = true;
      document.body.style.overflow = '';
    };
    modal.querySelectorAll('[data-notice-modal-close]').forEach(function(el) {
      el.onclick = closeFn;
    });
  }

  function onShareClick() {
    if (!getState().length) {
      showNotice(window.g26t('configurator.notice_empty_title', 'Canvas ist leer'), window.g26t('configurator.notice_empty_before_share', 'Bitte fügen Sie zuerst Stücke aus den Kategorien hinzu, bevor Sie Ihr Outfit teilen.'));
      return;
    }
    showSharePreview();
  }

  function defaultOutfitName() {
    var d = new Date();
    var pad = function(n) { return n < 10 ? '0' + n : n; };
    return window.g26t('configurator.default_outfit_name', 'Mein Outfit {{ date }}', { date: pad(d.getDate()) + '.' + pad(d.getMonth() + 1) + '.' + d.getFullYear() });
  }

  // Saves the canvas as a saved outfit (Merkliste page + account), updating the record this page was opened
  // from instead of duplicating it. Shared by the save icon and the guided "Alles auf die Merkliste" (r191).
  function saveOutfitRecord(name) {
    var O = window.Goessl && window.Goessl.outfits;
    if (!O) return null;
    var existing = currentOutfitId ? O.getOutfit(currentOutfitId) : null;

    // Capture full layout so reopening from favs reproduces exactly what
    // the user composed — positions, sizes, color, variant.
    var items = getState().map(function(item) {
      var o = {
        handle: item.handle,
        variant_id: item.variant_id,
        category_slot: item.collection,
        sizeScale: item.sizeScale != null ? item.sizeScale : null,
        colorCode: item.colorCode || null
      };
      if (item.customX != null) o.customX = item.customX;
      if (item.customY != null) o.customY = item.customY;
      if (item.customW != null) o.customW = item.customW;
      if (item.customH != null) o.customH = item.customH;
      if (item.sizeSet) o.size_set = true; // r191: the customer chose this size - reopen it as chosen
      return o;
    });

    var qrEl = document.querySelector('[data-canvas-qr]');
    var qrLayout = null;
    if (qrEl && qrEl.dataset.dragged === 'true') {
      qrLayout = {
        x: parseFloat(qrEl.dataset.normX || 0),
        y: parseFloat(qrEl.dataset.normY || 0)
      };
    }

    if (existing) {
      // Update in place — no duplicate record.
      O.updateOutfit(currentOutfitId, {
        name: name || existing.name,
        items: items,
        gender: currentGender,
        qr_layout: qrLayout
      });
      return currentOutfitId;
    }
    var created = O.createOutfit(name, items, currentGender);
    if (created && created.id) {
      currentOutfitId = created.id;
      // createOutfit's signature doesn't take qr_layout — patch it on.
      if (qrLayout) O.updateOutfit(created.id, { qr_layout: qrLayout });
    }
    return currentOutfitId;
  }

  function bindBottomBar() {
    if (saveBtn) {
      saveBtn.addEventListener('click', function() {
        if (!getState().length) {
          showNotice(window.g26t('configurator.notice_empty_title', 'Canvas ist leer'), window.g26t('configurator.notice_empty_before_save', 'Bitte fügen Sie zuerst Stücke aus den Kategorien hinzu, bevor Sie Ihr Outfit speichern.'));
          return;
        }
        if (!window.Goessl || !window.Goessl.outfits) return;
        // r39g (SJ): saving outfits, like the Merkliste, needs an account.
        if (window.Goessl.wishlist && !window.Goessl.wishlist.isLoggedIn()) {
          window.Goessl.wishlist.promptSignIn();
          return;
        }

        var existing = currentOutfitId ? window.Goessl.outfits.getOutfit(currentOutfitId) : null;
        var defaultName = defaultOutfitName();
        var promptDefault = existing ? existing.name : defaultName;
        var name = prompt(window.g26tText('configurator.outfit_name_prompt', 'Outfit-Name:'), promptDefault);
        if (name === null) return;
        name = name.trim() || defaultName;

        saveOutfitRecord(name);

        saveBtn.style.color = '#fff';
        saveBtn.style.background = 'var(--goessl-green-brand, #2d5016)';
        setTimeout(function() { saveBtn.style.color = ''; saveBtn.style.background = ''; }, 1500);
      });
    }

    if (shareBtn) {
      shareBtn.addEventListener('click', onShareClick);
    }

    if (cartBtn) {
      cartBtn.addEventListener('click', function() {
        if (!getState().length) {
          showNotice(window.g26t('configurator.notice_empty_title', 'Canvas ist leer'), window.g26t('configurator.notice_empty_add_first', 'Bitte fügen Sie zuerst Stücke aus den Kategorien hinzu.'));
          return;
        }
        openSizeModal();
      });
    }
  }

  // ═══════════════════════════════════════════
  //  SIZE SELECTION MODAL
  // ═══════════════════════════════════════════

  var sizeModal, sizeModalItems, sizeModalConfirm;
  var selectedVariants = {};

  function openSizeModal() {
    sizeModal = sizeModal || document.querySelector('[data-size-modal]');
    sizeModalItems = sizeModalItems || document.querySelector('[data-size-modal-items]');
    sizeModalConfirm = sizeModalConfirm || document.querySelector('[data-size-modal-confirm]');
    if (!sizeModal) return;

    selectedVariants = {};
    sizeModalItems.replaceChildren();

    getState().forEach(function(stateItem, stateIdx) {
      var product = productCache[stateItem.handle];
      if (!product) return;
      var itemKey = stateIdx + '_' + stateItem.handle;

      var row = document.createElement('div');
      row.className = 'outfit-cfg__size-item';

      var displayImg = null;
      if (stateItem.colorCode) {
        displayImg = findImageForColor(product, stateItem.colorCode);
      }
      if (!displayImg && product.images && product.images[0]) {
        displayImg = product.images[0];
      }
      if (displayImg) {
        var img = document.createElement('img');
        img.className = 'outfit-cfg__size-item-img';
        img.src = displayImg.src.replace(/\.([^.]+)$/, '_200x.$1');
        img.alt = product.title || '';
        row.appendChild(img);
      }

      var info = document.createElement('div');
      info.className = 'outfit-cfg__size-item-info';

      var title = document.createElement('p');
      title.className = 'outfit-cfg__size-item-title';
      title.textContent = product.title;
      info.appendChild(title);

      if (product.variants && product.variants[0]) {
        var price = document.createElement('p');
        price.className = 'outfit-cfg__size-item-price';
        price.textContent = money(product.variants[0].price);
        info.appendChild(price);
      }

      var options = document.createElement('div');
      options.className = 'outfit-cfg__size-item-options';

      // r71: same colour filter as the selected-panel dropdown (26.08 fix) —
      // deduping across ALL colourways bound '38' to the first colour with
      // that size and the cart got a colour the canvas never showed.
      // r191: one entry per Schnitt + size (sizeEntriesFor).
      var sizeEntries = sizeEntriesFor(product, stateItem.colorCode).filter(function(e) { return e.label; });

      sizeEntries.forEach(function(entry) {
        var v = entry.variant;
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'outfit-cfg__size-option';
        // available may be null — only mark unavailable if EXPLICITLY false
        if (v.available === false) btn.className += ' is-unavailable';
        // r191: the size already on the item shows as chosen (it is what the cart gets)
        if (stateItem.variant_id === v.id) btn.className += ' is-selected';
        btn.textContent = entry.label;

        btn.addEventListener('click', function() {
          if (v.available === false) return;
          options.querySelectorAll('.outfit-cfg__size-option').forEach(function(b) { b.classList.remove('is-selected'); });
          btn.classList.add('is-selected');
          selectedVariants[itemKey] = v.id;
          stateItem.variant_id = v.id;
          markSizeChosen(stateItem);
          checkAllSizesSelected();
        });

        options.appendChild(btn);
      });

      info.appendChild(options);
      row.appendChild(info);
      sizeModalItems.appendChild(row);

      if (stateItem.variant_id) selectedVariants[itemKey] = stateItem.variant_id;
    });

    sizeModalConfirm.onclick = confirmSizeAndCart;
    document.querySelectorAll('[data-size-modal-close]').forEach(function(el) { el.onclick = closeSizeModal; });

    checkAllSizesSelected();
    sizeModal.hidden = false;
    document.body.style.overflow = 'hidden';
  }

  function closeSizeModal() {
    if (sizeModal) sizeModal.hidden = true;
    document.body.style.overflow = '';
  }

  function checkAllSizesSelected() {
    var allSelected = getState().every(function(item, idx) {
      return selectedVariants[idx + '_' + item.handle];
    });
    if (sizeModalConfirm) sizeModalConfirm.disabled = !allSelected;
  }

  function confirmSizeAndCart() {
    var cartItems = [];
    var cartHandles = [];
    getState().forEach(function(item, idx) {
      var vid = selectedVariants[idx + '_' + item.handle];
      if (!vid) return;
      cartHandles.push(item.handle);
      cartItems.push(cartLineFor(item, vid));
    });
    if (!cartItems.length) return;

    sizeModalConfirm.textContent = window.g26tText('products.product.adding', 'Wird hinzugefügt...');
    sizeModalConfirm.disabled = true;

    postOutfitLines(cartItems, cartHandles)
    .then(function() {
      closeSizeModal();
    })
    .catch(function(err) {
      console.error('Outfit cart add failed:', err);
      sizeModalConfirm.textContent = window.g26tText('general.accessibility.error', 'Fehler');
      sizeModalConfirm.disabled = false;
      setTimeout(function() { sizeModalConfirm.textContent = window.g26tText('products.product.add_to_cart', 'In den Warenkorb'); sizeModalConfirm.disabled = false; }, 2000);
    });
  }

  // ═══════════════════════════════════════════
  //  SHARE
  // ═══════════════════════════════════════════

  // Compute the effective layout for the current state: same algorithm
  // renderCanvas uses, returning a map keyed by the item's original index in
  // state[]. This lets getShareUrl serialize the EXACT positions a viewer
  // would see, regardless of viewport. Without this, a desktop user sharing
  // an unmodified outfit would have empty position data in the URL, and the
  // mobile recipient's auto-layout might choose different positions than the
  // desktop layout if the canvas aspect ratio ever drifted (it's currently
  // 1:1 on both, but better safe than have a discrepancy bug later).
  function getEffectivePositions() {
    var state = getState();
    var sorted = state.map(function(item, idx) {
      return { item: item, origIdx: idx };
    }).sort(function(a, b) {
      return (b.item.sizeScale || 0.7) - (a.item.sizeScale || 0.7);
    });
    var count = Math.min(sorted.length, 8);
    var grid = GRID_LAYOUTS[count] || GRID_LAYOUTS[8];
    var effective = {};
    sorted.forEach(function(entry, gridIdx) {
      var cell = grid[gridIdx];
      if (!cell) return;
      var scale = entry.item.sizeScale || 0.7;
      var cellW = cell.w * scale;
      var cellH = cell.h * scale;
      effective[entry.origIdx] = {
        x: cell.x + (cell.w - cellW) / 2,
        y: cell.y + (cell.h - cellH) / 2,
        w: cellW,
        h: cellH
      };
    });
    return effective;
  }

  function getShareUrl() {
    var state = getState();
    var origin = window.location.origin + window.location.pathname;

    var qrEl = document.querySelector('[data-canvas-qr]');
    var qrMoved = qrEl && qrEl.dataset.dragged === 'true';
    var effective = getEffectivePositions();

    // Always use the rich form so positions, sizes, colors, and variants
    // are explicit. Receiver renders the exact layout the sender saw,
    // regardless of viewport. URL gets shortened by mintShortUrl for the
    // QR code, so length isn't a UX concern.
    var payload = {
      g: currentGender[0],
      i: state.map(function(item, idx) {
        var eff = effective[idx] || {};
        var o = { h: item.handle };
        if (item.collection) o.c = item.collection;
        if (item.variant_id) o.v = item.variant_id;
        if (item.sizeScale != null) o.s = Math.round(item.sizeScale * 1000);
        if (item.colorCode) o.cc = item.colorCode;
        // Always include explicit positions/sizes — use custom if set, else
        // computed effective from the grid algorithm.
        var posX = item.customX != null ? item.customX : eff.x;
        var posY = item.customY != null ? item.customY : eff.y;
        var sizeW = item.customW != null ? item.customW : eff.w;
        var sizeH = item.customH != null ? item.customH : eff.h;
        if (posX != null) o.x = Math.round(posX * 1000);
        if (posY != null) o.y = Math.round(posY * 1000);
        if (sizeW != null) o.w = Math.round(sizeW * 1000);
        if (sizeH != null) o.H = Math.round(sizeH * 1000);
        return o;
      })
    };
    if (qrMoved) {
      payload.q = {
        x: Math.round(parseFloat(qrEl.dataset.normX || 0) * 1000),
        y: Math.round(parseFloat(qrEl.dataset.normY || 0) * 1000)
      };
    }
    var encoded = btoa(JSON.stringify(payload))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    return origin + '?l=' + encoded;
  }

  // The QR and screenshot libraries are theme assets (#914, 18.10.2026: a visitor's IP goes to no third-party CDN).
  // A .js asset cannot run Liquid, so sections/outfit-configurator.liquid hands over their URLs as data-lib-* on
  // the section root. No CDN fallback on purpose: a missing URL is a theme bug and says so in the console.
  function vendorLibUrl(name) {
    var root = document.querySelector('[data-outfit-configurator]');
    var url = root && root.getAttribute('data-lib-' + name);
    if (!url) console.error('[configurator] No URL for the ' + name + ' library: data-lib-' + name + ' is missing on [data-outfit-configurator] (sections/outfit-configurator.liquid).');
    return url;
  }

  // QR code generation: lazy-load qrcode-generator, render onto a canvas.
  // Called whenever the outfit changes so the QR always encodes the current share URL.
  var qrLibLoaded = false;
  function loadQrLib() {
    if (qrLibLoaded || window.qrcode) { qrLibLoaded = true; return Promise.resolve(); }
    var src = vendorLibUrl('qrcode');
    if (!src) return Promise.reject(new Error('qrcode library URL missing'));
    return new Promise(function(resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = function() { qrLibLoaded = true; resolve(); };
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  function updateCanvasQR() {
    var qrEl = document.querySelector('[data-canvas-qr]');
    if (!qrEl) return;
    var longUrl = getShareUrl();
    // Tag the QR element with the URL we're about to render so we can
    // detect stale callbacks if the outfit changes mid-mint.
    qrEl.dataset.pendingUrl = longUrl;
    // Mint a short URL so the QR encodes ~50 chars instead of 300+.
    // mintShortUrl is cached by long-URL signature, so this is cheap on re-render.
    mintShortUrl(longUrl).then(function(url) {
      if (qrEl.dataset.pendingUrl !== longUrl) return; // stale — newer state in flight
      renderQrSvg(qrEl, url);
    });
  }

  function renderQrSvg(qrEl, url) {
    loadQrLib().then(function() {
      // Level M tolerates ~15% damage — sufficient now that the brand mark
      // sits standalone above the canvas, not inside the QR.
      var qr = window.qrcode(0, 'M');
      qr.addData(url);
      qr.make();
      var modules = qr.getModuleCount();
      var SVG_NS = 'http://www.w3.org/2000/svg';
      var size = modules; // unitless; SVG viewBox handles scaling
      // Build an SVG (vector — sharp at any rendered size, no pixelation)
      var existing = qrEl.querySelector('svg.outfit-cfg__qr-svg');
      if (existing) existing.remove();
      var svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('class', 'outfit-cfg__qr-svg');
      svg.setAttribute('viewBox', '0 0 ' + size + ' ' + size);
      svg.setAttribute('xmlns', SVG_NS);
      svg.setAttribute('aria-label', 'Outfit QR Code');
      svg.setAttribute('shape-rendering', 'crispEdges');
      // White background
      var bg = document.createElementNS(SVG_NS, 'rect');
      bg.setAttribute('x', 0); bg.setAttribute('y', 0);
      bg.setAttribute('width', size); bg.setAttribute('height', size);
      bg.setAttribute('fill', '#ffffff');
      svg.appendChild(bg);
      // Dark modules
      for (var r = 0; r < modules; r++) {
        for (var c = 0; c < modules; c++) {
          if (qr.isDark(r, c)) {
            var rect = document.createElementNS(SVG_NS, 'rect');
            rect.setAttribute('x', c);
            rect.setAttribute('y', r);
            rect.setAttribute('width', 1);
            rect.setAttribute('height', 1);
            rect.setAttribute('fill', '#00561C');
            svg.appendChild(rect);
          }
        }
      }
      // Insert BEFORE the logo so logo overlays on top
      qrEl.insertBefore(svg, qrEl.firstChild);
    }).catch(function() {
      // Library load failed — clear SVG only, keep the logo overlay
      var existing = qrEl.querySelector('svg.outfit-cfg__qr-svg');
      if (existing) existing.remove();
    });
  }

  function loadFromUrl() {
    var params = new URLSearchParams(window.location.search);
    var oid = params.get('oid');
    var rich = params.get('l');
    var compact = params.get('o');
    var legacy = params.get('outfit');
    var addHandle = params.get('add'); // PDP → configurator handoff: ?add={handle}

    // Single-handle add from a PDP "Zum Konfigurator" button
    if (addHandle && !oid && !rich && !compact && !legacy) {
      loadHandlesOntoCanvas(addHandle.split(',').filter(Boolean), 'add');
      return;
    }

    // Reopen a saved outfit from localStorage with full layout.
    // !! DO NOT REMOVE !! (wishlist.js race)
    // Defer-script execution order between layout-included wishlist.js and
    // section-included outfit-configurator.js is not guaranteed across all
    // browsers (confirmed by SJ console: "window.Goessl.outfits not available").
    // If oid is present but the outfits API hasn't booted yet, poll briefly
    // (max 2s) and retry. After that, give up and fall through to other
    // params so the page doesn't hang.
    if (oid && (!window.Goessl || !window.Goessl.outfits)) {
      var tries = 0;
      var maxTries = 40; // 40 × 50ms = 2s
      var retryTimer = setInterval(function() {
        tries++;
        if (window.Goessl && window.Goessl.outfits) {
          clearInterval(retryTimer);
          loadFromUrl(); // re-enter with API now available
        } else if (tries >= maxTries) {
          clearInterval(retryTimer);
          console.warn('[configurator] window.Goessl.outfits never became available — saved outfit cannot be loaded.');
        }
      }, 50);
      return;
    }

    if (oid) console.log('[configurator] Loading saved outfit by oid:', oid);
    if (oid && window.Goessl && window.Goessl.outfits) {
      var saved = window.Goessl.outfits.getOutfit(oid);
      console.log('[configurator] getOutfit result:', saved);
      if (saved) {
        currentOutfitId = oid;
        currentGender = saved.gender || 'frauen';
        setGenderUI();
        var specs = (saved.items || [])
          .filter(function(it) { return it && it.handle; }) // !! drop items without handle (PDP outfits sometimes saved with empty handle)
          .map(function(it) {
            var o = { h: it.handle };
            if (it.category_slot || it.collection) o.c = it.category_slot || it.collection;
            if (it.variant_id) o.v = it.variant_id;
            if (it.sizeScale != null) o.s = Math.round(it.sizeScale * 1000);
            if (it.colorCode) o.cc = it.colorCode;
            if (it.customX != null) o.x = Math.round(it.customX * 1000);
            if (it.customY != null) o.y = Math.round(it.customY * 1000);
            if (it.customW != null) o.w = Math.round(it.customW * 1000);
            if (it.customH != null) o.H = Math.round(it.customH * 1000);
            if (it.size_set) o.z = 1; // r191: saved outfits only - a share link never says the size is chosen
            return o;
          });
        console.log('[configurator] Loading', specs.length, 'items from saved outfit. specs:', specs);
        if (!specs.length) {
          console.warn('[configurator] Outfit has no items with valid handles. Saved data:', saved);
        }
        var qrSpec = saved.qr_layout ? {
          x: Math.round((saved.qr_layout.x || 0) * 1000),
          y: Math.round((saved.qr_layout.y || 0) * 1000)
        } : null;
        loadHandlesOntoCanvasWithLayout(specs, qrSpec, 'outfit');
        return;
      } else {
        console.warn('[configurator] No saved outfit found with id:', oid, 'Available outfits:',
          window.Goessl.outfits.getOutfits ? window.Goessl.outfits.getOutfits().map(function(o){return o.id}) : '(getOutfits not exposed)');
      }
    }

    if (rich) {
      try {
        var b64 = rich.replace(/-/g, '+').replace(/_/g, '/');
        while (b64.length % 4) b64 += '=';
        var payload = JSON.parse(atob(b64));
        currentGender = payload.g === 'm' ? 'maenner' : 'frauen';
        setGenderUI();
        loadHandlesOntoCanvasWithLayout(payload.i || [], payload.q || null, 'outfit');
        return;
      } catch (e) {}
    }

    if (compact) {
      var parts = compact.split(':');
      if (parts.length >= 2) {
        currentGender = parts[0] === 'm' ? 'maenner' : 'frauen';
        setGenderUI();
        loadHandlesOntoCanvas(parts[1].split(',').filter(Boolean), 'outfit');
      }
    } else if (legacy) {
      try {
        var decoded = JSON.parse(atob(legacy));
        if (decoded.gender) { currentGender = decoded.gender; setGenderUI(); }
        if (decoded.items) loadHandlesOntoCanvas(decoded.items.map(function(i) { return i.handle; }), 'outfit');
      } catch (e) {}
    }
  }

  // Like loadHandlesOntoCanvas but applies positions/sizes/color/variant from URL spec.
  // kind ('outfit' | 'add') tells the guided path where to open once the pieces are on the canvas (r191).
  function loadHandlesOntoCanvasWithLayout(specs, qrSpec, kind) {
    var picks = sessionRead(SIZE_PICKS_KEY) || {};
    var fetches = specs.map(function(spec) {
      return fetch(window.g26url('products/' + encodeURIComponent(spec.h) + '.json') + '?_fd=0')
        .then(function(r) { return r.ok ? r.json() : null; })
        .then(function(data) {
          if (!data || !data.product) return null;
          productCache[data.product.handle] = data.product;
          return { product: data.product, spec: spec };
        }).catch(function() { return null; });
    });

    Promise.all(fetches).then(function(results) {
      results.forEach(function(r) {
        if (!r) return;
        var product = r.product, spec = r.spec;
        var collection = spec.c || guessCollectionForProduct(product);
        var variantId = spec.v ||
          (product.variants && product.variants[0] ? product.variants[0].id : null);
        var item = {
          handle: product.handle,
          collection: collection,
          variant_id: variantId,
          sizeScale: spec.s != null ? spec.s / 1000 : getCategorySize(collection),
          colorCode: spec.cc || null
        };
        if (spec.x != null) item.customX = spec.x / 1000;
        if (spec.y != null) item.customY = spec.y / 1000;
        if (spec.w != null) item.customW = spec.w / 1000;
        if (spec.H != null) item.customH = spec.H / 1000;
        // r191: a size counts as chosen when the saved outfit says so, or when THIS visitor picked exactly
        // this variant earlier in the session (the ?l= URL is also the reload state) - never from a share link alone
        if (spec.v && (spec.z || picks[product.handle] === spec.v)) item.sizeSet = true;
        getState().push(item);
      });
      renderCanvas();
      if (kind) guideAfterLoad(kind);
      updateUI();
      if (qrSpec) applyQrLayout(qrSpec);
    });
  }

  function setGenderUI() {
    // Gender toggle UI removed — currentGender is derived from category selection.
    // When loading a saved/shared outfit with a gender hint, jump to a sensible
    // default category for that gender so the dropdown isn't stale.
    var select = document.querySelector('.outfit-cfg__cat-select');
    if (!select) return;
    var defaults = { frauen: 'frauen-dirndl', maenner: 'maenner-lederhosen', kinder: 'kinder-dirndl' };
    var target = defaults[currentGender] || 'frauen-dirndl';
    var has = false;
    for (var i = 0; i < select.options.length; i++) {
      if (select.options[i].value === target) { has = true; break; }
    }
    if (has) {
      select.value = target;
      showProductGrid(target);
    }
  }

  function loadHandlesOntoCanvas(handles, kind) {
    var fetches = handles.map(function(spec) {
      // spec may be "handle" or "handle:colorCode" (PDP / newsletter handoff)
      var sep = String(spec).indexOf(':');
      var handle = sep === -1 ? String(spec) : String(spec).slice(0, sep);
      var wantColor = sep === -1 ? null : String(spec).slice(sep + 1);
      return fetch(window.g26url('products/' + encodeURIComponent(handle) + '.json') + '?_fd=0')
        .then(function(r) { return r.ok ? r.json() : null; })
        .then(function(data) {
          if (!data || !data.product) return null;
          productCache[data.product.handle] = data.product;
          return { product: data.product, wantColor: wantColor };
        }).catch(function() { return null; });
    });

    Promise.all(fetches).then(function(results) {
      results.forEach(function(res) {
        if (!res || !res.product) return;
        var product = res.product;
        var variants = product.variants || [];
        // Pick the variant for the requested colour; otherwise the first variant.
        var variant = variants[0] || null;
        if (res.wantColor) {
          for (var k = 0; k < variants.length; k++) {
            if (extractColorCode(variants[k].option1) === res.wantColor) { variant = variants[k]; break; }
          }
        }
        // Always derive colorCode from the chosen variant so the canvas shows the
        // matching colour image (renderCanvas keys off colorCode, not variant_id).
        var colorCode = variant ? extractColorCode(variant.option1) : null;
        var guessedCollection = guessCollectionForProduct(product);

        getState().push({
          handle: product.handle,
          collection: guessedCollection,
          variant_id: variant ? variant.id : null,
          colorCode: colorCode,
          sizeScale: getCategorySize(guessedCollection)
        });
      });
      renderCanvas();
      if (kind) guideAfterLoad(kind);
      updateUI();
    });
  }

  function slugifyTag(s) {
    return String(s || '').toLowerCase()
      .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function guessCollectionForProduct(product) {
    // r191: the product's own category tags first - "Frauen Dirndlblusen" -> frauen-dirndlblusen,
    // "Männer Lederhose" -> maenner-lederhosen, "Männer Gilet" -> maenner-gilets - matched against the
    // configurator's real categories (the select), the earliest category winning. The substring guess
    // below filed every Dirndlbluse under frauen-dirndl ('dirndl' is checked first) and every Lederhose
    // under accessoires; it stays as the fallback for products without such a tag.
    var known = [];
    document.querySelectorAll('.outfit-cfg__cat-select option').forEach(function(o) { if (o.value) known.push(o.value); });
    if (guide.cfg) guide.cfg.paths.forEach(function(p) { p.steps.forEach(function(s) { s.collections.forEach(function(c) { if (known.indexOf(c.handle) === -1) known.push(c.handle); }); }); });
    var best = -1;
    tagList(product).forEach(function(t) {
      var slug = slugifyTag(t);
      [slug, slug + 'n', slug + 's'].forEach(function(cand) {
        var i = known.indexOf(cand);
        if (i > -1 && (best === -1 || i < best)) best = i;
      });
    });
    if (best > -1) return known[best];

    // Try to guess collection from product type or tags.
    // Shopify's /products/{handle}.json returns tags as comma-separated STRING,
    // while the Storefront/Admin API returns an array. Handle both shapes.
    var ptype = (product.product_type || '').toLowerCase();
    var rawTags = product.tags;
    var tags = Array.isArray(rawTags) ? rawTags.join(' ') : (rawTags || '').toString();
    tags = tags.toLowerCase();
    var combined = ptype + ' ' + tags;
    var cats = Object.keys(CATEGORY_SIZES);
    for (var i = 0; i < cats.length; i++) {
      if (combined.indexOf(cats[i]) !== -1) return currentGender + '-' + cats[i];
    }
    return currentGender + '-accessoires';
  }

  // ═══════════════════════════════════════════
  //  SCREENSHOT + R2 UPLOAD
  // ═══════════════════════════════════════════

  function showSharePreview() {
    var canvas = document.querySelector('[data-outfit-canvas]');
    if (!canvas) return;

    // !! EMERGENCY BYPASS — iOS Safari html2canvas keeps hanging.
    // On any iOS device, skip the screenshot entirely and open the share
    // popup immediately with a 1x1 placeholder. Facebook/Pinterest/
    // Instagram/Link kopieren all work off the share URL. Bild speichern
    // produces a 1x1 PNG (acceptable trade-off until server-side
    // renderer ships).
    var isIOS = /iP(hone|ad|od)/i.test(navigator.userAgent) ||
                (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (isIOS) {
      var placeholder = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkAAIAAAoAAv/lxKUAAAAASUVORK5CYII=';
      try { openSharePopup(placeholder); } catch (e) { console.warn('share popup error', e); }
      return;
    }

    shareBtn.style.opacity = '0.5';
    shareBtn.style.pointerEvents = 'none';

    var actionsEl = canvas.querySelector('.outfit-cfg__canvas-actions');
    if (actionsEl) actionsEl.style.display = 'none';
    canvas.classList.add('is-capturing'); // disables mix-blend-mode on item images

    // Force opacity:1 on each canvas-item AND its inner <img> directly via
    // inline style. iOS Safari + html2canvas-pro can otherwise read a
    // mid-animation opacity value, producing a faded screenshot. Inline
    // style outranks the keyframe so both stay visually solid.
    var capturedItems = canvas.querySelectorAll('.outfit-cfg__canvas-item');
    var savedStyles = [];
    Array.prototype.forEach.call(capturedItems, function(el) {
      savedStyles.push({ el: el, opacity: el.style.opacity });
      el.style.opacity = '1';
      var img = el.querySelector('img');
      if (img) {
        savedStyles.push({ el: img, opacity: img.style.opacity });
        img.style.opacity = '1';
      }
    });

    function cleanup() {
      shareBtn.style.opacity = '';
      shareBtn.style.pointerEvents = '';
      if (actionsEl) actionsEl.style.display = '';
      canvas.classList.remove('is-capturing');
      savedStyles.forEach(function(s) { s.el.style.opacity = s.opacity; });
    }

    // Wait for every <img> inside the canvas to load before capture.
    var canvasImages = canvas.querySelectorAll('img');
    var imagesReady = Promise.all(Array.prototype.map.call(canvasImages, function(img) {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise(function(resolve) {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      });
    }));

    // Watchdog — recover after 10s if anything stalls.
    var stalled = false;
    var watchdog = setTimeout(function() {
      stalled = true;
      cleanup();
      console.warn('[configurator] Share capture stalled — recovered.');
      if (typeof showNotice === 'function') {
        try { showNotice(window.g26t('configurator.share_failed_title', 'Teilen nicht möglich'), window.g26t('configurator.share_failed_message', 'Bitte versuchen Sie es erneut.')); }
        catch (e) {}
      }
    }, 10000);

    imagesReady
      .then(loadHtml2Canvas)
      .then(function() { return new Promise(function(r) { setTimeout(r, 250); }); })
      .then(function() {
        if (stalled) throw new Error('aborted');
        // Simplest possible html2canvas call. Past attempts to override
        // width/height/x/y/windowWidth caused mis-sized/cropped captures
        // (SJ confirmed: capture rendered as a tiny top-left tile with
        // most of the area as empty cream). Let html2canvas measure the
        // element itself.
        return html2canvas(canvas, {
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#f0ede8',
          scale: Math.min(window.devicePixelRatio || 1, 2)
          // foreignObjectRendering: true was tried — produces a blank PNG
          // on iOS Safari due to cross-origin image security restrictions
          // in SVG <foreignObject>. Stick with the default raster path; the
          // mix-blend-mode + opacity workarounds (is-capturing class +
          // inline opacity:1) handle the fade.
        });
      })
      .then(function(rendered) {
        if (stalled) return;
        clearTimeout(watchdog);
        cleanup();
        openSharePopup(rendered.toDataURL('image/png'));
      })
      .catch(function(err) {
        clearTimeout(watchdog);
        cleanup();
        console.error('Share capture failed:', err);
      });
  }

  // SVG icons for the share destinations (inline so the popup is self-contained).
  var SHARE_ICONS = {
    facebook: '<svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.52 1.49-3.91 3.78-3.91 1.09 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.78-1.63 1.57v1.88h2.78l-.44 2.91h-2.34V22c4.78-.76 8.43-4.92 8.43-9.94Z"/></svg>',
    pinterest: '<svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M12.04 2C6.5 2 2 6.5 2 12.04c0 4.25 2.64 7.88 6.37 9.34-.09-.79-.17-2.02.04-2.89.18-.78 1.16-4.97 1.16-4.97s-.3-.6-.3-1.48c0-1.39.81-2.43 1.81-2.43.85 0 1.27.64 1.27 1.4 0 .86-.55 2.14-.83 3.32-.23.99.5 1.8 1.48 1.8 1.78 0 3.14-1.88 3.14-4.59 0-2.4-1.72-4.07-4.18-4.07-2.85 0-4.52 2.13-4.52 4.34 0 .86.33 1.78.74 2.28.08.1.09.18.07.28-.07.31-.24.99-.27 1.13-.04.18-.14.22-.32.13-1.21-.56-1.96-2.32-1.96-3.74 0-3.04 2.21-5.84 6.37-5.84 3.34 0 5.94 2.38 5.94 5.57 0 3.32-2.1 6-5.01 6-.98 0-1.9-.51-2.21-1.11l-.6 2.29c-.22.84-.81 1.89-1.21 2.53.91.28 1.87.43 2.86.43 5.54 0 10.04-4.5 10.04-10.04C22.08 6.5 17.58 2 12.04 2Z"/></svg>',
    instagram: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor"/></svg>',
    download: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M12 4v12"/><path d="M7 11l5 5 5-5"/><path d="M5 21h14"/></svg>',
    link: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1"/><path d="M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1"/></svg>'
  };

  // Build the social-button popup. Uploads to R2 once, mints short URL once,
  // then offers per-destination buttons that reuse those values.
  function openSharePopup(dataUrl) {
    // dataUrl might be a 1×1 placeholder (iOS popup-open bypass) or a real
    // screenshot (desktop). Anything that consumes the image is now lazy:
    // first button click triggers html2canvas if needed, result is cached.
    var IS_PLACEHOLDER = dataUrl && dataUrl.length < 200;

    // Lazy real-screenshot — runs html2canvas on iOS only on first call.
    var realDataUrlPromise = null;
    function getRealDataUrl() {
      if (realDataUrlPromise) return realDataUrlPromise;
      if (!IS_PLACEHOLDER) {
        realDataUrlPromise = Promise.resolve(dataUrl);
        return realDataUrlPromise;
      }
      realDataUrlPromise = captureCanvasOnDemand();
      return realDataUrlPromise;
    }

    // Lazy file (for navigator.share — Instagram + iMessage share sheet).
    var filePromise = null;
    function getFile() {
      if (filePromise) return filePromise;
      filePromise = getRealDataUrl().then(function(du) {
        var byteString = atob(du.split(',')[1]);
        var ab = new ArrayBuffer(byteString.length);
        var ia = new Uint8Array(ab);
        for (var i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
        var blob = new Blob([ab], { type: 'image/png' });
        return new File([blob], 'goessl-outfit.png', { type: 'image/png' });
      });
      return filePromise;
    }

    // Lazy R2 upload — Pinterest needs the public image URL.
    var r2UrlPromise = null;
    function getR2Url() {
      if (r2UrlPromise) return r2UrlPromise;
      r2UrlPromise = getRealDataUrl().then(function(du) {
        return fetch(R2_UPLOAD_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ image: du })
        }).then(function(r) { return r.json(); }).then(function(data) {
          if (data && data.url) return data.url;
          throw new Error((data && data.error) || 'r2_upload_failed');
        });
      });
      return r2UrlPromise;
    }

    // Three URLs at play:
    //   • shortUrl (gsl.asksj.com/s/...) → QR code only (density)
    //   • humanShareUrl (goessl.com/...?l=...) → Copy-link button (clean
    //     branded URL the user pastes anywhere)
    //   • ogUrl (gsl.asksj.com/o/...) → Facebook + Pinterest. When their
    //     crawlers hit it, the Worker serves HTML with og:image pointing at
    //     the outfit screenshot, so link previews actually show the outfit.
    //     Humans clicking the same link get 302'd to goessl.com.
    var humanShareUrl = getShareUrl();
    var shortUrlPromise = mintShortUrl(humanShareUrl);

    // Lazy ogUrl — depends on r2 upload completing first.
    var ogUrlPromise = null;
    function getOgUrl() {
      if (ogUrlPromise) return ogUrlPromise;
      ogUrlPromise = getR2Url().then(function(imageUrl) {
        return mintOgUrl(humanShareUrl, imageUrl);
      });
      return ogUrlPromise;
    }

    // On-demand capture using NATIVE Canvas API — bypasses html2canvas
    // entirely so it works reliably on iOS Safari. PRE-LOADS each product
    // image via fetch + Blob → Object URL so the canvas isn't tainted by
    // cross-origin Shopify CDN images (which would block toDataURL).
    function captureCanvasOnDemand() {
      var canvasEl = document.querySelector('[data-outfit-canvas]');
      if (!canvasEl) return Promise.reject(new Error('no canvas'));
      var rect = canvasEl.getBoundingClientRect();
      var size = Math.round(Math.min(rect.width, rect.height) || 600);
      var dpr = Math.min(window.devicePixelRatio || 1, 2);

      // Helper: load image with crossOrigin=anonymous so drawImage() doesn't
      // taint the canvas. Shopify CDN sets Access-Control-Allow-Origin: *
      // so this just works. Falls back to the live <img> reference if the
      // CORS load fails — drawImage will succeed but toDataURL will throw,
      // caught at the toDataURL site.
      function loadCorsImage(src, fallbackImg) {
        return new Promise(function(resolve) {
          var im = new Image();
          im.crossOrigin = 'anonymous';
          im.onload = function() { resolve(im); };
          im.onerror = function() {
            console.warn('CORS image load failed, falling back:', src);
            resolve(fallbackImg || null);
          };
          im.src = src;
        });
      }

      function svgToImage(svgEl, forcedFill) {
        // Clone so we don't mutate the live DOM element. The serialized SVG
        // is rendered standalone (no parent stylesheet), so any CSS-driven
        // fill colour falls back to black. Force the fill via inline
        // attributes on all child elements + the root.
        var clone = svgEl.cloneNode(true);
        if (forcedFill) {
          clone.setAttribute('fill', forcedFill);
          var nodes = clone.querySelectorAll('*');
          for (var i = 0; i < nodes.length; i++) {
            // Only override if the node doesn't already have an explicit
            // non-black fill (preserves multi-colour SVGs like QR codes).
            var existing = nodes[i].getAttribute('fill');
            if (!existing || existing === 'currentColor' || existing === '') {
              nodes[i].setAttribute('fill', forcedFill);
            }
          }
        }
        var svgStr = new XMLSerializer().serializeToString(clone);
        var blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
        var url = URL.createObjectURL(blob);
        return new Promise(function(resolve, reject) {
          var im = new Image();
          im.onload = function() { resolve(im); };
          im.onerror = function() { URL.revokeObjectURL(url); reject(new Error('svg load failed')); };
          im.src = url;
        });
      }

      // Collect items and pre-fetch product images via CORS so canvas
      // doesn't get tainted.
      var items = Array.prototype.slice.call(canvasEl.querySelectorAll('.outfit-cfg__canvas-item'));
      var itemPlans = items.map(function(item) {
        var img = item.querySelector('img');
        if (!img || !img.naturalWidth || !img.src) return null;
        var iR = item.getBoundingClientRect();
        var ix = iR.left - rect.left;
        var iy = iR.top - rect.top;
        var iw = iR.width;
        var ih = iR.height;
        return {
          src: img.src,
          aspect: img.naturalWidth / img.naturalHeight,
          ix: ix, iy: iy, iw: iw, ih: ih
        };
      }).filter(Boolean);

      var brandEl = canvasEl.querySelector('.outfit-cfg__canvas-brand');
      var qrEl = canvasEl.querySelector('.outfit-cfg__canvas-qr');

      return Promise.all(itemPlans.map(function(plan) {
        return loadCorsImage(plan.src).then(function(im) {
          plan.image = im;
          return plan;
        });
      })).then(function(loadedPlans) {
        var c = document.createElement('canvas');
        c.width = size * dpr;
        c.height = size * dpr;
        var ctx = c.getContext('2d');
        ctx.scale(dpr, dpr);

        // Background
        ctx.fillStyle = '#f5f3ef';
        ctx.fillRect(0, 0, size, size);

        // Products
        loadedPlans.forEach(function(plan) {
          if (!plan.image) return;
          var dw, dh;
          if (plan.iw / plan.ih > plan.aspect) { dh = plan.ih; dw = plan.ih * plan.aspect; }
          else { dw = plan.iw; dh = plan.iw / plan.aspect; }
          var dx = plan.ix + (plan.iw - dw) / 2;
          var dy = plan.iy + (plan.ih - dh) / 2;
          ctx.save();
          ctx.globalCompositeOperation = 'multiply';
          try { ctx.drawImage(plan.image, dx, dy, dw, dh); }
          catch (e) { console.warn('drawImage failed:', e); }
          ctx.restore();
        });

        // Border
        ctx.strokeStyle = '#00561C';
        ctx.lineWidth = 2;
        ctx.strokeRect(13, 13, size - 26, size - 26);
        ctx.strokeStyle = 'rgba(0, 86, 28, 0.5)';
        ctx.lineWidth = 1;
        ctx.strokeRect(17, 17, size - 34, size - 34);

        // Brand + QR (SVG → Image)
        var asyncDraws = [];
        if (brandEl) {
          var brandSvg = brandEl.querySelector('svg');
          if (brandSvg) {
            var bR = brandEl.getBoundingClientRect();
            // Force Gössl green — the live SVG fills via CSS which doesn't
            // apply to a serialized standalone SVG.
            asyncDraws.push(svgToImage(brandSvg, '#00561C').then(function(im) {
              ctx.drawImage(im, bR.left - rect.left, bR.top - rect.top, bR.width, bR.height);
            }).catch(function() { /* skip */ }));
          }
        }
        if (qrEl) {
          var qR = qrEl.getBoundingClientRect();
          var qx = qR.left - rect.left, qy = qR.top - rect.top;
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(qx, qy, qR.width, qR.height);
          var qrSvg = qrEl.querySelector('svg');
          var qrCanvas = qrEl.querySelector('canvas');
          if (qrSvg) {
            asyncDraws.push(svgToImage(qrSvg).then(function(im) {
              var pad = 8;
              ctx.drawImage(im, qx + pad, qy + pad, qR.width - pad * 2, qR.height - pad * 2);
            }).catch(function() { /* skip */ }));
          } else if (qrCanvas) {
            var pad2 = 8;
            try { ctx.drawImage(qrCanvas, qx + pad2, qy + pad2, qR.width - pad2 * 2, qR.height - pad2 * 2); }
            catch (e) {}
          }
        }

        return Promise.all(asyncDraws).then(function() {
          try { return c.toDataURL('image/png'); }
          catch (e) {
            console.error('toDataURL failed (canvas tainted):', e);
            throw e;
          }
        });
      });
    }

    var overlay = document.createElement('div');
    overlay.className = 'outfit-cfg__share-preview';

    var card = document.createElement('div');
    card.className = 'outfit-cfg__share-preview-card outfit-cfg__share-popup';

    var title = document.createElement('p');
    title.className = 'outfit-cfg__share-preview-title';
    title.textContent = window.g26tText('configurator.share_where', 'Wo möchten Sie teilen?');
    card.appendChild(title);

    var img = document.createElement('img');
    img.src = dataUrl;
    img.className = 'outfit-cfg__share-preview-img';
    card.appendChild(img);

    var grid = document.createElement('div');
    grid.className = 'outfit-cfg__share-grid';
    card.appendChild(grid);

    function close() { overlay.remove(); document.body.style.overflow = ''; }

    function makeDestButton(opts) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'outfit-cfg__share-dest outfit-cfg__share-dest--' + opts.kind;
      // the label is translated text (g26tText, decoded): it goes in as text, never as markup
      btn.innerHTML = '<span class="outfit-cfg__share-dest-icon">' + opts.icon + '</span><span class="outfit-cfg__share-dest-label"></span>';
      btn.querySelector('.outfit-cfg__share-dest-label').textContent = opts.label;
      btn.addEventListener('click', function() {
        if (opts.busyText) {
          var labelEl = btn.querySelector('.outfit-cfg__share-dest-label');
          if (labelEl) labelEl.textContent = opts.busyText;
          btn.disabled = true;
        }
        try { opts.onClick(btn); } catch (e) { console.error('Share destination failed:', e); }
      });
      return btn;
    }

    // Facebook — share the OG-aware URL so FB scrapes the outfit screenshot
    // for the link preview. og:url points at goessl.com so the visible domain
    // in the post is "goessl.com".
    grid.appendChild(makeDestButton({
      kind: 'facebook', icon: SHARE_ICONS.facebook, label: 'Facebook',
      busyText: window.g26tText('configurator.share_uploading', 'Bild wird hochgeladen…'),
      onClick: function(btn) {
        getOgUrl().then(function(ogUrl) {
          var fb = 'https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(ogUrl);
          window.open(fb, '_blank', 'noopener,noreferrer,width=640,height=520');
          var labelEl = btn.querySelector('.outfit-cfg__share-dest-label');
          if (labelEl) labelEl.textContent = 'Facebook';
          btn.disabled = false;
        }).catch(function() {
          var labelEl = btn.querySelector('.outfit-cfg__share-dest-label');
          if (labelEl) labelEl.textContent = window.g26tText('general.accessibility.error', 'Fehler');
          btn.disabled = false;
        });
      }
    }));

    // Pinterest — pin creator with image + OG-aware URL. Pinterest crawls
    // url= for additional metadata, so the OG endpoint gives them a clean
    // og:image + goessl.com og:url even though the actual click goes through
    // gsl.asksj.com first.
    grid.appendChild(makeDestButton({
      kind: 'pinterest', icon: SHARE_ICONS.pinterest, label: 'Pinterest',
      busyText: window.g26tText('configurator.share_uploading', 'Bild wird hochgeladen…'),
      onClick: function(btn) {
        Promise.all([getR2Url(), getOgUrl()]).then(function(vals) {
          var imageUrl = vals[0], ogUrl = vals[1];
          var pin = 'https://www.pinterest.com/pin/create/button/'
            + '?url=' + encodeURIComponent(ogUrl)
            + '&media=' + encodeURIComponent(imageUrl)
            + '&description=' + encodeURIComponent(window.g26t('configurator.share_title', 'Mein Gössl Outfit'));
          window.open(pin, '_blank', 'noopener,noreferrer,width=720,height=620');
          var labelEl = btn.querySelector('.outfit-cfg__share-dest-label');
          if (labelEl) labelEl.textContent = 'Pinterest';
          btn.disabled = false;
        }).catch(function() {
          var labelEl = btn.querySelector('.outfit-cfg__share-dest-label');
          if (labelEl) labelEl.textContent = window.g26tText('general.accessibility.error', 'Fehler');
          btn.disabled = false;
        });
      }
    }));

    // Instagram — IG can't accept inbound shares from the web. On mobile with
    // navigator.share + file, IG appears in the system share sheet. On desktop,
    // we save the image and prompt the user to upload it manually.
    grid.appendChild(makeDestButton({
      kind: 'instagram', icon: SHARE_ICONS.instagram, label: 'Instagram',
      busyText: window.g26tText('configurator.share_preparing', 'Wird vorbereitet…'),
      onClick: function(btn) {
        var labelEl = btn.querySelector('.outfit-cfg__share-dest-label');
        Promise.all([getFile(), shortUrlPromise]).then(function(vals) {
          var file = vals[0], shortUrl = vals[1];
          if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
            return buildShareText(shortUrl).then(function(shareText) {
              return navigator.share({
                title: window.g26tText('configurator.share_title', 'Mein Gössl Outfit'),
                text: shareText,
                files: [file]
              });
            }).then(function() { close(); }).catch(function(err) {
              if (labelEl) labelEl.textContent = 'Instagram';
              btn.disabled = false;
              if (err && err.name === 'AbortError') return;
            });
          }
          // Desktop fallback — save image and prompt user to upload manually.
          return getRealDataUrl().then(function(du) {
            triggerDownload(du, 'goessl-outfit.png');
            if (labelEl) labelEl.textContent = window.g26tText('configurator.share_saved_upload_instagram', 'Bild gespeichert · auf Instagram hochladen');
            setTimeout(function() {
              if (labelEl) labelEl.textContent = 'Instagram';
              btn.disabled = false;
            }, 3500);
          });
        }).catch(function() {
          if (labelEl) labelEl.textContent = window.g26tText('general.accessibility.error', 'Fehler');
          btn.disabled = false;
        });
      }
    }));

    // Save image — local PNG download. Uses the lazy native-canvas
    // capture so iOS gets a real screenshot the same way Pinterest +
    // Instagram do.
    grid.appendChild(makeDestButton({
      kind: 'download', icon: SHARE_ICONS.download, label: window.g26tText('configurator.share_save_image', 'Bild speichern'),
      busyText: window.g26tText('configurator.share_creating', 'Wird erstellt …'),
      onClick: function(btn) {
        var labelEl = btn.querySelector('.outfit-cfg__share-dest-label');
        getRealDataUrl().then(function(du) {
          triggerDownload(du, 'goessl-outfit.png');
          if (labelEl) labelEl.textContent = window.g26tText('configurator.share_saved', 'Gespeichert ✓');
          setTimeout(function() {
            if (labelEl) labelEl.textContent = window.g26tText('configurator.share_save_image', 'Bild speichern');
            btn.disabled = false;
          }, 2000);
        }).catch(function(err) {
          console.error('Save failed:', err);
          if (labelEl) labelEl.textContent = window.g26tText('general.accessibility.error', 'Fehler');
          setTimeout(function() {
            if (labelEl) labelEl.textContent = window.g26tText('configurator.share_save_image', 'Bild speichern');
            btn.disabled = false;
          }, 2500);
        });
      }
    }));

    // Copy link — copies the share text + goessl.com URL together so when
    // the user pastes into a chat/post they get a captioned preview ready.
    grid.appendChild(makeDestButton({
      kind: 'link', icon: SHARE_ICONS.link, label: window.g26tText('configurator.share_copy_link', 'Link kopieren'),
      busyText: window.g26tText('configurator.share_preparing', 'Wird vorbereitet…'),
      onClick: function(btn) {
        if (!navigator.clipboard) return;
        var labelEl = btn.querySelector('.outfit-cfg__share-dest-label');
        shortUrlPromise.then(function(shortUrl) {
          return buildShareText(shortUrl);
        }).then(function(shareText) {
          return navigator.clipboard.writeText(shareText);
        }).then(function() {
          if (labelEl) labelEl.textContent = window.g26tText('configurator.share_copied', 'Kopiert ✓');
          setTimeout(function() {
            if (labelEl) labelEl.textContent = window.g26tText('configurator.share_copy_link', 'Link kopieren');
            btn.disabled = false;
          }, 2000);
        }).catch(function() {
          if (labelEl) labelEl.textContent = window.g26tText('general.accessibility.error', 'Fehler');
          btn.disabled = false;
        });
      }
    }));

    var cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'outfit-cfg__share-cancel';
    cancel.textContent = window.g26tText('customer.addresses.cancel', 'Abbrechen');
    cancel.addEventListener('click', close);
    card.appendChild(cancel);

    overlay.appendChild(card);
    overlay.addEventListener('click', function(e) {
      if (e.target === overlay) close();
    });
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';
  }

  function triggerDownload(dataUrl, filename) {
    var a = document.createElement('a');
    a.href = dataUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }


  function captureAndUpload() {
    var canvas = document.querySelector('[data-outfit-canvas]');
    if (!canvas) return Promise.reject('No canvas');

    // Temporarily unfix canvas for html2canvas
    var wrap = canvas.closest('.outfit-cfg__canvas-wrap');
    var savedPosition = wrap ? wrap.style.cssText : '';
    if (wrap && getComputedStyle(wrap).position === 'fixed') {
      wrap.style.position = 'absolute';
      wrap.style.bottom = 'auto';
      wrap.style.top = '0';
      wrap.style.left = '0';
      wrap.style.width = wrap.offsetWidth + 'px';
      wrap.style.height = wrap.offsetHeight + 'px';
      // Move offscreen to hide during capture. Avoid `opacity` — html2canvas
      // honors computed opacity and would bake it into the rendered image,
      // producing a faded/transparent screenshot.
      wrap.style.left = '-100000px';
      wrap.style.zIndex = '-1';
    }

    return loadHtml2Canvas().then(function() {
      return html2canvas(canvas, { useCORS: true, allowTaint: true, scale: 2, backgroundColor: null });
    }).then(function(rendered) {
      if (wrap) wrap.style.cssText = savedPosition;
      var dataUrl = rendered.toDataURL('image/png');
      return fetch(R2_UPLOAD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image: dataUrl })
      });
    }).then(function(r) { return r.json(); }).then(function(data) {
      if (data.url) return data.url;
      throw new Error(data.error || 'Upload failed');
    });
  }

  // Customer-facing article numbers for the share text come from the
  // goessl.article_no metafield (handle prefixes are group_ids \u2014 an internal
  // number that means nothing to Kundendienst or the stores). Cached per
  // handle; a piece without a resolvable number simply gets no "(Art. \u2026)".
  var articleNoCache = {};
  function fetchArticleNumbers(handles) {
    var missing = handles.filter(function(h) { return !(h in articleNoCache); });
    if (!missing.length) return Promise.resolve(articleNoCache);
    return fetch('https://gwandl-mcp.g-ssl.workers.dev/storefront/articles?handles=' + encodeURIComponent(missing.join(',')))
      .then(function(r) { return r.ok ? r.json() : { articles: {} }; })
      .catch(function() { return { articles: {} }; })
      .then(function(d) {
        missing.forEach(function(h) { articleNoCache[h] = (d.articles && d.articles[h]) || null; });
        return articleNoCache;
      });
  }

  // Async since the article numbers may need one fetch (callers are all
  // inside promise chains already).
  function buildShareText(shareUrl) {
    var state = getState();
    return fetchArticleNumbers(state.map(function(i) { return i.handle; })).then(function(map) {
      var lines = [window.g26t('configurator.share_title', 'Mein Gössl Outfit') + '\n'];
      state.forEach(function(item) {
        var product = productCache[item.handle];
        if (!product) return;
        var art = map[item.handle];
        lines.push('\u2022 ' + product.title + (art ? ' ' + window.g26t('configurator.share_article_no', '(Art. {{ number }})', { number: art }) : ''));
      });
      // Caller must pass the gsl.asksj.com short URL \u2014 pasting the long
      // ?l=\u2026 URL into chat clients (WhatsApp, Messenger, SMS) gets truncated
      // mid-base64 and the recipient lands on a partial outfit. mintShortUrl
      // already falls back to the long URL if the worker is unreachable, so
      // a missing argument here would defeat the purpose.
      lines.push('\n' + window.g26tText('configurator.share_view_outfit', 'Outfit ansehen:') + ' ' + (shareUrl || getShareUrl()));
      return lines.join('\n');
    });
  }

  function shareImage(imageUrl) {
    return mintShortUrl(getShareUrl()).then(function(outfitUrl) {
    if (navigator.share && navigator.canShare) {
      return fetch(imageUrl).then(function(r) { return r.blob(); }).then(function(blob) {
        var file = new File([blob], 'goessl-outfit.png', { type: 'image/png' });
        var shareData = { title: window.g26tText('configurator.share_title', 'Mein Gössl Outfit'), text: window.g26tText('configurator.share_invite', 'Mein Gössl Outfit – stellen Sie Ihres zusammen:') + ' ' + outfitUrl, files: [file] };
        if (navigator.canShare(shareData)) return navigator.share(shareData);
        return navigator.share({ title: window.g26tText('configurator.share_title', 'Mein Gössl Outfit'), url: outfitUrl });
      }).catch(function() {});
    }
    if (navigator.clipboard) {
      navigator.clipboard.writeText(imageUrl);
      if (shareBtn) { shareBtn.textContent = window.g26tText('configurator.image_link_copied', 'Bild-Link kopiert!'); setTimeout(function() { shareBtn.textContent = window.g26tText('general.social.share', 'Teilen'); }, 2000); }
    }
    window.open(imageUrl, '_blank');
    return Promise.resolve();
    });
  }

  var html2canvasLoaded = false;
  function loadHtml2Canvas() {
    if (html2canvasLoaded) return Promise.resolve();
    var src = vendorLibUrl('html2canvas');
    if (!src) return Promise.reject(new Error('html2canvas library URL missing'));
    return new Promise(function(resolve, reject) {
      var s = document.createElement('script');
      // html2canvas-pro: actively maintained fork with iOS Safari color
      // management fixes (oklch, alpha blending, CSS backdrop-filter).
      s.src = src;
      s.onload = function() { html2canvasLoaded = true; resolve(); };
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  // ═══════════════════════════════════════════
  //  GUIDED PATH (r191 — Felix 22.09, SJ 05.10)
  // ═══════════════════════════════════════════
  //
  // A thin layer over the free canvas, not a second configurator: the same state, cards, canvas, share link,
  // cart and Merkliste code. The steps are DATA (data-guide-config in the section: paths -> steps ->
  // collections), so nothing here names a category or a gender. One piece per step (a new pick takes the
  // old one's place on the canvas), every step can be skipped, the last step picks every size, then the
  // whole outfit goes to the cart or the Merkliste. A piece stays changeable afterwards: tap its step, tap
  // it on the canvas, or "Ändern" beside its sizes. The free arrangement is one tap away (mode tabs under
  // the title) and shares the canvas 1:1 - positions stay fractions, so share links keep working (r11b).

  var MODE_KEY = 'goessl_cfg_mode';        // sessionStorage: the mode this visitor last chose
  var SIZE_PICKS_KEY = 'goessl_cfg_sizes'; // sessionStorage: handle -> the variant this visitor chose a size for

  function sessionRead(key) {
    try { return JSON.parse(window.sessionStorage.getItem(key) || 'null'); } catch (e) { return null; }
  }
  function sessionWrite(key, val) {
    try { window.sessionStorage.setItem(key, JSON.stringify(val)); } catch (e) { /* private window: it just forgets */ }
  }

  function gt(key, vars) {
    var s = (guide.cfg && guide.cfg.t && guide.cfg.t[key] != null) ? String(guide.cfg.t[key]) : '';
    if (vars) Object.keys(vars).forEach(function(k) {
      var v = String(vars[k]);
      s = s.replace(new RegExp('\\{\\{\\s*' + k + '\\s*\\}\\}', 'g'), function() { return v; });
    });
    return s;
  }

  // The size memory family the PDP uses (r154: gender + kind from the tags), so a size picked here is the
  // size the PDP preselects next time, and the other way round. Men's shirts (and the Kragenweite Schnitt of
  // a shirt-jacket) are sized by collar width and have their own family - one rule for the whole shop since
  // r192: GoesslPersonal.sizes.familyFor reads all sizes of the product and the variant's Schnitt; r197 (#353): and the
  // variant's SKU, which decides - the Schnitt's value is translated on /en ("Collar size").
  function sizeFamilyOf(product, v) {
    var P = window.GoesslPersonal;
    if (!product || !P || !P.sizes || !P.sizes.familyFor) return null;
    var sizes = (product.variants || []).map(function(x) { return rawSizeOfVariant(product, x); });
    var meta = { tags: tagList(product), type: product.product_type || product.type, title: product.title };
    var skus = (v ? [v] : (product.variants || [])).map(function(x) { return x.sku; });
    return P.sizes.familyFor(meta, sizes, v ? cutOfVariant(product, v) : '', skus);
  }

  // A size the visitor picked through any door (guided step, panel dropdown, size modal, quick view): shown
  // as chosen, kept for a reload in this session, and handed to the size memory.
  function markSizeChosen(item) {
    if (!item || !item.variant_id) return;
    item.sizeSet = true;
    delete item.sizeFrom;
    var picks = sessionRead(SIZE_PICKS_KEY) || {};
    picks[item.handle] = item.variant_id;
    sessionWrite(SIZE_PICKS_KEY, picks);
    var product = productCache[item.handle];
    var v = variantById(product, item.variant_id);
    var fam = sizeFamilyOf(product, v);
    var P = window.GoesslPersonal;
    if (!fam || !P || !P.sizes || !P.sizes.remember) return;
    var raw = v && rawSizeOfVariant(product, v);
    if (raw) P.sizes.remember(fam, raw);
  }

  function variantById(product, id) {
    var vs = (product && product.variants) || [];
    for (var i = 0; i < vs.length; i++) if (vs[i].id === id) return vs[i];
    return null;
  }

  // /products/{handle}.json (share links, saved outfits, the PDP handoff) has no `available` per variant; the
  // last step asks /products/{handle}.js once per piece so a sold-out size is struck, not offered.
  var availCache = {}, availPending = {};
  function variantAvailable(product, v) {
    var a = availCache[product.handle];
    if (a && Object.prototype.hasOwnProperty.call(a, v.id)) return a[v.id];
    return v.available !== false; // gotcha #3: unknown counts as available
  }
  function guideLoadAvailability() {
    getState().forEach(function(it) {
      var p = productCache[it.handle];
      if (!p || availCache[it.handle] || availPending[it.handle]) return;
      if (!(p.variants || []).some(function(v) { return typeof v.available !== 'boolean'; })) return;
      availPending[it.handle] = true;
      fetch(window.g26url('products/' + encodeURIComponent(it.handle) + '.js'))
        .then(function(r) { if (!r.ok) throw new Error('product.js ' + r.status); return r.json(); })
        .then(function(d) {
          var m = {};
          (d.variants || []).forEach(function(v) { m[v.id] = v.available !== false; });
          availCache[it.handle] = m;
          if (guide.active && onSizesStep()) renderGuide();
        })
        .catch(function() { /* unknown stays available, as before r191 */ })
        .then(function() { delete availPending[it.handle]; });
    });
  }

  // `| t` HTML-escapes its output ("Gilet & Jacke" arrives as "Gilet &amp; Jacke") and every guide string
  // is written with textContent, so the config is decoded once here. A DOMParser document is inert: it
  // runs no script and loads nothing, it only turns entities back into characters.
  var entityParser = typeof DOMParser !== 'undefined' ? new DOMParser() : null;
  function decodeEntities(s) {
    s = String(s == null ? '' : s);
    if (!entityParser || s.indexOf('&') === -1) return s;
    return entityParser.parseFromString(s, 'text/html').documentElement.textContent || s;
  }

  function guideParse() {
    var node = document.querySelector('[data-guide-config]');
    if (!node) return null;
    var cfg;
    try { cfg = JSON.parse(node.textContent); } catch (e) { return null; }
    if (!cfg || !cfg.paths) return null;
    Object.keys(cfg.t || {}).forEach(function(k) { cfg.t[k] = decodeEntities(cfg.t[k]); });
    cfg.paths.forEach(function(p) {
      p.label = decodeEntities(p.label);
      p.steps = (p.steps || []).map(function(s) {
        s.label = decodeEntities(s.label);
        s.collections = (s.collections || []).filter(function(c) { return c && c.handle && c.count > 0; });
        s.collections.forEach(function(c) { c.label = decodeEntities(c.label); });
        return s;
      }).filter(function(s) { return s.collections.length; });
    });
    cfg.paths = cfg.paths.filter(function(p) { return p.steps.length; });
    return cfg.paths.length ? cfg : null;
  }

  function gSteps() { return guide.cfg.paths[guide.path].steps; }
  function onSizesStep() { return guide.step >= gSteps().length; }

  // The step a canvas piece belongs to, by the collection it was picked from; -1 = an extra piece
  function stepIndexOf(collection, path) {
    var steps = (path || guide.cfg.paths[guide.path]).steps;
    for (var i = 0; i < steps.length; i++) {
      for (var j = 0; j < steps[i].collections.length; j++) {
        if (steps[i].collections[j].handle === collection) return i;
      }
    }
    return -1;
  }
  function itemsOfStep(i) { return getState().filter(function(it) { return stepIndexOf(it.collection) === i; }); }

  function firstOpenStep() {
    var steps = gSteps();
    for (var i = 0; i < steps.length; i++) {
      if (!itemsOfStep(i).length && !guide.skipped[steps[i].id]) return i;
    }
    return steps.length;
  }

  // The path that holds most of the canvas pieces; with none, the gender of the link or the last category
  function pathForState() {
    var best = -1, bestN = 0;
    guide.cfg.paths.forEach(function(p, pi) {
      var n = getState().filter(function(it) { return stepIndexOf(it.collection, p) > -1; }).length;
      if (n > bestN) { bestN = n; best = pi; }
    });
    if (best > -1) return best;
    for (var i = 0; i < guide.cfg.paths.length; i++) if (guide.cfg.paths[i].id === currentGender) return i;
    return guide.path;
  }

  function adoptPathGender() {
    var id = guide.cfg.paths[guide.path].id;
    if (/^(frauen|maenner|kinder)$/.test(id)) currentGender = id; // save metadata + the share link's g
  }

  // selectProduct asks: does this pick replace the piece of its step? (index in state, or -1)
  function guideReplaceIndex(collection) {
    if (!guide.active || !guide.cfg) return -1;
    var i = stepIndexOf(collection);
    if (i === -1) return -1;
    var st = getState();
    for (var k = 0; k < st.length; k++) if (stepIndexOf(st[k].collection) === i) return k;
    return -1;
  }

  // A shared or saved outfit opens on the last step (it is complete: sizes, then cart or Merkliste);
  // a piece handed over from a product page opens the first step it leaves open (Dirndl -> Schürze).
  function guideAfterLoad(kind) {
    if (!guide.cfg) return;
    guide.path = pathForState();
    adoptPathGender();
    guide.skipped = {};
    guide.optionsKey = '';
    guide.step = kind === 'add' ? firstOpenStep() : gSteps().length;
  }

  function guideInit() {
    guide.root = document.querySelector('[data-guide]');
    if (!guide.root) return;
    guide.cfg = guideParse();
    if (!guide.cfg) return; // no usable step: the page stays the free configurator it was
    guide.el = {
      paths: document.querySelector('[data-guide-paths]'),
      modeSwitch: document.querySelector('[data-cfg-mode-switch]'),
      steps: guide.root.querySelector('[data-guide-steps]'),
      body: guide.root.querySelector('[data-guide-body]'),
      chips: guide.root.querySelector('[data-guide-chips]'),
      info: guide.root.querySelector('[data-guide-info]'),
      colors: guide.root.querySelector('[data-guide-colors]'),
      options: guide.root.querySelector('[data-guide-options]'),
      sizes: guide.root.querySelector('[data-guide-sizes]'),
      foot: guide.root.querySelector('[data-guide-foot]')
    };
    var emptyP = canvasEmpty && canvasEmpty.querySelector('p');
    guide.freeEmpty = emptyP ? emptyP.textContent : '';

    guide.cfg.paths.forEach(function(p, pi) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'outfit-cfg__mode outfit-cfg__guide-path';
      b.textContent = p.label;
      b.setAttribute('aria-pressed', 'false');
      b.addEventListener('click', function() { setPath(pi); });
      guide.el.paths.appendChild(b);
    });
    for (var i = 0; i < guide.cfg.paths.length; i++) {
      if (guide.cfg.paths[i].id === currentGender) { guide.path = i; break; }
    }

    var modes = document.querySelector('[data-cfg-modes]');
    if (modes) modes.hidden = false;
    if (guide.el.modeSwitch) {
      guide.el.modeSwitch.addEventListener('click', function() { setMode(guide.active ? 'free' : 'guided', true); });
    }

    setMode(sessionRead(MODE_KEY) === 'free' ? 'free' : 'guided', false);
  }

  function setMode(mode, remember) {
    if (!guide.cfg) return;
    guide.active = mode !== 'free';
    var root = document.querySelector('[data-outfit-configurator]');
    if (root) root.setAttribute('data-mode', guide.active ? 'guided' : 'free');
    guide.root.hidden = !guide.active;
    // Damen / Herren belong to the guided path; the switch names the other way of working
    guide.el.paths.hidden = !guide.active || guide.cfg.paths.length < 2;
    if (guide.el.modeSwitch) guide.el.modeSwitch.textContent = guide.active ? gt('mode_free') : gt('mode_guided');
    if (remember) sessionWrite(MODE_KEY, guide.active ? 'guided' : 'free');
    var emptyP = canvasEmpty && canvasEmpty.querySelector('p');
    if (emptyP) emptyP.textContent = guide.active ? gt('canvas_empty') : guide.freeEmpty;
    if (guide.active) {
      guide.optionsKey = '';
      renderGuide();
    } else {
      // the free panel and grid were not redrawn while hidden
      renderSelectedPanel();
      updateTotal();
      highlightSelectedCards();
      applyFilters();
    }
  }

  function setPath(pi) {
    if (pi === guide.path) return;
    guide.path = pi;
    adoptPathGender();
    guide.skipped = {};
    guide.family = {}; // a colour family belongs to its step and gender
    guide.optionsKey = '';
    goStep(firstOpenStep(), true);
  }

  function goStep(i, user) {
    guide.step = Math.max(0, Math.min(i, gSteps().length));
    guide.showMissing = false;
    renderGuide();
    if (!user) return;
    if (guide.el.body.scrollTop) guide.el.body.scrollTop = 0; // the desktop panel scrolls inside itself
    guideScrollIntoView();
  }

  // On a phone the guide sits under the canvas: after a step change keep the step's controls in view and
  // as much of the canvas above them as fits; the last step starts at the step bar.
  function guideScrollIntoView() {
    if (window.matchMedia && window.matchMedia('(min-width: 1025px)').matches) return;
    var canvasEl = document.querySelector('[data-outfit-canvas]');
    if (!canvasEl) return;
    var headerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    var y = window.pageYOffset;
    var target;
    if (onSizesStep()) {
      target = guide.root.getBoundingClientRect().top + y - headerH - 8;
    } else {
      var canvasTop = canvasEl.getBoundingClientRect().top + y - headerH - 8;
      var guideBottom = guide.root.getBoundingClientRect().bottom + y;
      target = Math.max(canvasTop, guideBottom - window.innerHeight + 16);
    }
    if (Math.abs(target - y) > 24) window.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
  }

  // A tap on a canvas piece opens its step (an extra piece: the last step, where it is listed)
  function guideOpenStepOf(item) {
    if (!guide.cfg) return;
    var i = stepIndexOf(item.collection);
    goStep(i === -1 ? gSteps().length : i, true);
  }

  function renderGuide() {
    if (!guide.active || !guide.cfg) return;
    guide.el.paths.querySelectorAll('.outfit-cfg__guide-path').forEach(function(b, i) {
      b.setAttribute('aria-pressed', i === guide.path ? 'true' : 'false');
    });
    renderGuideSteps();
    var sizes = onSizesStep();
    guide.el.options.hidden = sizes;
    guide.el.sizes.hidden = !sizes;
    if (sizes) {
      guide.el.chips.hidden = true;
      guide.el.info.hidden = true;
      guide.el.colors.hidden = true;
      renderGuideSizes();
    } else {
      renderGuideOptions();
    }
    renderGuideFoot();
  }

  function renderGuideSteps() {
    var ol = guide.el.steps;
    ol.replaceChildren();
    var steps = gSteps();
    steps.concat([{ label: gt('step_sizes') }]).forEach(function(s, i) {
      var li = document.createElement('li');
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'outfit-cfg__guide-step';
      if (i === guide.step) b.setAttribute('aria-current', 'step');
      if (i < steps.length && itemsOfStep(i).length) b.classList.add('is-done');
      var no = document.createElement('span');
      no.className = 'outfit-cfg__guide-step-no';
      no.textContent = String(i + 1);
      var lb = document.createElement('span');
      lb.className = 'outfit-cfg__guide-step-label';
      lb.textContent = s.label;
      b.appendChild(no);
      b.appendChild(lb);
      b.addEventListener('click', function() { goStep(i, true); });
      li.appendChild(b);
      ol.appendChild(li);
    });
  }

  // The Länge the other steps' pieces set for this step (a Dirndl of 70 cm -> Schürzen of 70 cm): the same
  // rule as isLengthCompatible - the step's own piece does not count, it is about to be replaced.
  function stepLength(i) {
    var step = gSteps()[i];
    if (!step || !step.collections.some(function(c) { return isLengthSensitive(c.handle); })) return null;
    var st = getState();
    for (var k = 0; k < st.length; k++) {
      if (stepIndexOf(st[k].collection) === i) continue;
      var len = pieceLength(productCache[st[k].handle]);
      if (len) return len;
    }
    return null;
  }

  function guideTextButton(label, onClick) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'outfit-cfg__guide-link';
    b.textContent = label;
    b.addEventListener('click', onClick);
    return b;
  }

  function guideMessage(text) {
    var p = document.createElement('p');
    p.className = 'outfit-cfg__loading';
    p.textContent = text;
    return p;
  }

  function renderGuideOptions() {
    var i = guide.step;
    var step = gSteps()[i];
    var coll = guide.chip[step.id];
    if (!step.collections.some(function(c) { return c.handle === coll; })) coll = step.collections[0].handle;
    guide.chip[step.id] = coll;

    var chips = guide.el.chips;
    chips.replaceChildren();
    chips.hidden = step.collections.length < 2;
    if (!chips.hidden) step.collections.forEach(function(c) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'outfit-cfg__guide-chip';
      b.textContent = c.label;
      b.setAttribute('aria-pressed', c.handle === coll ? 'true' : 'false');
      b.addEventListener('click', function() {
        if (guide.chip[step.id] !== c.handle) delete guide.family[step.id]; // another sub-collection: all colours again
        guide.chip[step.id] = c.handle;
        renderGuide();
      });
      chips.appendChild(b);
    });

    // Länge set by the other pieces: say so, and offer the way out
    var conLen = stepLength(i);
    var info = guide.el.info;
    info.replaceChildren();
    info.hidden = !conLen;
    if (conLen) {
      if (guide.allLen[step.id]) {
        info.appendChild(guideTextButton(gt('length_note', { length: conLen }), function() { guide.allLen[step.id] = false; renderGuide(); }));
      } else {
        var note = document.createElement('span');
        note.textContent = gt('length_note', { length: conLen });
        info.appendChild(note);
        info.appendChild(guideTextButton(gt('all_lengths'), function() { guide.allLen[step.id] = true; renderGuide(); }));
      }
    }
    var len = conLen && !guide.allLen[step.id] ? conLen : null;

    var key = [guide.path, step.id, coll, len || ''].join('|');
    if (key === guide.optionsKey) { highlightSelectedCards(); return; }
    guide.optionsKey = key;
    var box = guide.el.options;
    box.replaceChildren(guideMessage(gt('loading')));
    guide.el.colors.hidden = true; // the previous step's colours are not this step's
    loadCollection(coll).then(function(products) {
      // r192: with a Länge to match, a piece that carries it only in the metafield needs it before sorting
      if (!len || guide.optionsKey !== key) return products;
      return Promise.all(products.map(function(p) { return getDirndlLength(p) ? null : fetchLaenge(p.handle); }))
        .then(function() { return products; });
    }).then(function(products) {
      if (guide.optionsKey !== key) return; // a newer step is on screen
      box.replaceChildren();
      var addCard = function(product, pk) {
        var card = createMiniCard(product, coll, pk);
        card.dataset.families = cardFamilies(product, pk && pk.code).join(' ');
        box.appendChild(card);
      };
      products.forEach(function(product) {
        if (!product.images || !product.images.length) return;
        if (len) { var pl = pieceLength(product); if (pl && pl !== len) return; }
        // r112: one card per colour, exactly like the free grid
        var picks = colourPicksFor(product);
        if (picks.length > 1) picks.forEach(function(pk) { addCard(product, pk); });
        else addCard(product, null);
      });
      if (!box.children.length) box.appendChild(guideMessage(gt('no_products')));
      highlightSelectedCards();
      renderGuideColors(box, step);
      // bring the chosen piece into view inside the strip (or the panel), never by scrolling the page -
      // unless the colour filter hides it, then the row starts at its beginning
      var sel = box.querySelector('.goessl-product-card.is-selected:not([hidden])');
      // (strip and body are position: relative, so offsetLeft/offsetTop are relative to them)
      if (box.scrollWidth > box.clientWidth + 1) box.scrollLeft = sel ? Math.max(0, sel.offsetLeft - 16) : 0;
      else if (sel) guide.el.body.scrollTop = Math.max(0, box.offsetTop + sel.offsetTop - 16);
    }, function() {
      if (guide.optionsKey !== key) return;
      guide.optionsKey = '';
      box.replaceChildren(guideMessage(window.g26t('configurator.load_error', 'Fehler beim Laden.')));
    });
  }

  // r191b: the colour families of the free browser (colorFamilyOf, the same swatch chips) above a step's
  // pieces. Only the families the step has, one at a time, kept per step until the gender or the
  // sub-collection changes. Filtering only hides cards: a piece already chosen stays on the canvas.
  // A per-colour card belongs to its own colour; a single card to every colour of its product.
  function cardFamilies(product, code) {
    var fams = [];
    (product.variants || []).forEach(function(v) {
      if (code && extractColorCode(v.option1 || '') !== code) return;
      var f = colorFamilyOf(v.option1);
      if (f && fams.indexOf(f) === -1) fams.push(f);
    });
    return fams;
  }

  function renderGuideColors(box, step) {
    var row = guide.el.colors;
    var present = {};
    box.querySelectorAll('.goessl-product-card').forEach(function(c) {
      (c.dataset.families || '').split(' ').forEach(function(f) { if (f) present[f] = true; });
    });
    // the free browser's palette order; '0' (Sonstige) has no swatch there either
    var fams = ['1', '2', '3', '4', '5', '6', '7', '8', '9'].filter(function(f) { return present[f]; });
    if (!present[guide.family[step.id]]) delete guide.family[step.id];
    row.replaceChildren();
    row.hidden = fams.length < 2; // one family: nothing to narrow down
    if (row.hidden) delete guide.family[step.id]; // never a filter without its chips
    else {
      var all = document.createElement('button');
      all.type = 'button';
      all.className = 'outfit-cfg__filter-chip';
      all.dataset.filterAll = '';
      all.textContent = window.g26tText('configurator.filter_all', 'Alle');
      all.addEventListener('click', function() { setGuideFamily(''); });
      row.appendChild(all);
      fams.forEach(function(family) {
        var chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'outfit-cfg__filter-chip';
        chip.dataset.filterFamily = family;
        var dot = document.createElement('span');
        dot.className = 'outfit-cfg__filter-dot';
        dot.style.backgroundColor = COLOR_FAMILIES[family] || '#ccc';
        chip.appendChild(dot);
        var name = decodeEntities(COLOR_FAMILY_NAMES[family] || family);
        chip.title = name;
        chip.setAttribute('aria-label', name);
        // a second tap on the chosen family shows all again
        chip.addEventListener('click', function() { setGuideFamily(guide.family[step.id] === family ? '' : family); });
        row.appendChild(chip);
      });
    }
    applyGuideFamily(box, step);
  }

  function applyGuideFamily(box, step) {
    var fam = guide.family[step.id] || '';
    box.querySelectorAll('.goessl-product-card').forEach(function(c) {
      c.hidden = !!fam && (' ' + (c.dataset.families || '') + ' ').indexOf(' ' + fam + ' ') === -1;
    });
    guide.el.colors.querySelectorAll('.outfit-cfg__filter-chip').forEach(function(b) {
      var on = b.hasAttribute('data-filter-all') ? !fam : b.dataset.filterFamily === fam;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  function setGuideFamily(fam) {
    var step = gSteps()[guide.step];
    if (!step) return;
    if (fam) guide.family[step.id] = fam;
    else delete guide.family[step.id];
    var box = guide.el.options;
    applyGuideFamily(box, step);
    // the row of pieces starts over: the phone strip sideways, the desktop panel from the top
    box.scrollLeft = 0;
    if (box.scrollWidth <= box.clientWidth + 1) guide.el.body.scrollTop = 0;
  }

  function renderGuideFoot() {
    var foot = guide.el.foot;
    foot.replaceChildren();
    if (onSizesStep()) { renderGuideCartFoot(foot); return; }
    var i = guide.step;
    var has = itemsOfStep(i).length > 0;
    var back = guideTextButton(gt('back'), function() { goStep(i - 1, true); });
    back.hidden = i === 0;
    var next = document.createElement('button');
    next.type = 'button';
    next.className = 'btn outfit-cfg__guide-next' + (has ? '' : ' btn--secondary');
    next.textContent = has ? gt('next') : gt('skip');
    next.addEventListener('click', function() {
      if (!itemsOfStep(i).length) guide.skipped[gSteps()[i].id] = true;
      goStep(i + 1, true);
    });
    foot.appendChild(back);
    foot.appendChild(next);
  }

  function ensureColour(item, product) {
    // the panel's rule (SJ 26.08): no explicit colour = the first variant's colour, everywhere
    if (!item.colorCode && product.variants && product.variants[0]) {
      var c = extractColorCode(product.variants[0].option1 || '');
      if (c) item.colorCode = c;
    }
  }

  function needsSize(item) {
    var product = productCache[item.handle];
    if (!product) return false;
    var entries = sizeEntriesFor(product, item.colorCode).filter(function(e) { return e.label; });
    if (entries.length <= 1) return false; // one size (or none): nothing to choose
    var v = variantById(product, item.variant_id);
    return !item.sizeSet || !v || !variantAvailable(product, v);
  }

  // The remembered size (r154 size memory) for a piece nobody chose a size for yet - "Ihre Größe 38
  // vorausgewählt", and the apron size the memory derives from the Dirndl size (r160).
  function guidePreselect(item) {
    var product = productCache[item.handle];
    if (!product) return false;
    var cur = variantById(product, item.variant_id);
    if (item.sizeFrom && (!cur || !variantAvailable(product, cur))) { item.sizeSet = false; delete item.sizeFrom; }
    if (item.sizeSet) return false;
    var P = window.GoesslPersonal;
    if (!P || !P.sizes || !P.sizes.match) return false;
    var entries = sizeEntriesFor(product, item.colorCode).filter(function(e) { return e.label && variantAvailable(product, e.variant); });
    // one family per Schnitt: the Kragenweite and the Standardgröße of one jacket remember different scales
    var cuts = [];
    entries.forEach(function(e) { if (cuts.indexOf(e.cut) === -1) cuts.push(e.cut); });
    for (var i = 0; i < cuts.length; i++) {
      var group = entries.filter(function(e) { return e.cut === cuts[i]; });
      var fam = sizeFamilyOf(product, group[0].variant);
      var hit = fam ? P.sizes.match(fam, group.map(function(e) { return e.raw; })) : null;
      var pick = hit ? group.filter(function(e) { return e.raw === hit; })[0] : null;
      if (!pick) continue;
      var mem = (P.sizes.get && P.sizes.get(fam)) || {};
      item.variant_id = pick.variant.id;
      item.sizeSet = true;
      item.sizeFrom = { size: pick.size, derived: mem.derived || null };
      return true;
    }
    return false;
  }

  function colourNameOf(product, code) {
    var vs = (product.variants || []).filter(function(v) { return extractColorCode(v.option1 || '') === code; });
    return vs[0] ? String(vs[0].option1 || '').replace(/\s\d+$/, '').trim() : '';
  }

  function priceLineFor(product, item) {
    var vs = (product.variants || []).filter(function(v) { return !item.colorCode || extractColorCode(v.option1 || '') === item.colorCode; });
    var cur = item.sizeSet ? variantById(product, item.variant_id) : null;
    if (cur) return money(cur.price);
    var prices = vs.map(function(v) { return parseFloat(v.price); }).filter(function(n) { return isFinite(n); });
    if (!prices.length) return '';
    var min = Math.min.apply(null, prices), max = Math.max.apply(null, prices);
    return (max > min ? gt('price_from') + ' ' : '') + money(min);
  }

  function renderGuideSizes() {
    var box = guide.el.sizes;
    box.replaceChildren();
    var st = getState();
    if (!st.length) {
      var empty = document.createElement('p');
      empty.className = 'outfit-cfg__guide-hint';
      empty.textContent = gt('sizes_empty');
      box.appendChild(empty);
      return;
    }
    var changed = false;
    st.forEach(function(it) {
      var p = productCache[it.handle];
      if (p) ensureColour(it, p);
      if (guidePreselect(it)) changed = true;
    });
    if (changed) syncUrl(true);

    var hint = document.createElement('p');
    hint.className = 'outfit-cfg__guide-hint';
    hint.textContent = gt('sizes_hint');
    box.appendChild(hint);
    gSteps().forEach(function(s, i) {
      itemsOfStep(i).forEach(function(it) { box.appendChild(guideSizeRow(it, i)); });
    });
    var extras = st.filter(function(it) { return stepIndexOf(it.collection) === -1; });
    if (extras.length) {
      var h = document.createElement('p');
      h.className = 'outfit-cfg__guide-group';
      h.textContent = gt('extras');
      box.appendChild(h);
      extras.forEach(function(it) { box.appendChild(guideSizeRow(it, -1)); });
    }
    if (guide.focusKey) {
      var f = box.querySelector('[data-gsize-key="' + guide.focusKey + '"]');
      if (f) f.focus({ preventScroll: true });
      guide.focusKey = '';
    }
    guideLoadAvailability();
  }

  function guideSizeRow(item, stepIdx) {
    var product = productCache[item.handle];
    var row = document.createElement('div');
    row.className = 'outfit-cfg__gsize';
    if (!product) return row;
    var entries = sizeEntriesFor(product, item.colorCode).filter(function(e) { return e.label; });
    var missing = guide.showMissing && needsSize(item);
    if (missing) row.classList.add('is-missing');

    var thumb = document.createElement('div');
    thumb.className = 'outfit-cfg__selected-thumb';
    var img = (item.colorCode && findImageForColor(product, item.colorCode)) || (product.images && product.images[0]);
    if (img) {
      var im = document.createElement('img');
      im.src = img.src.replace(/\.([^.]+)$/, '_200x.$1');
      im.alt = product.title || '';
      im.loading = 'lazy';
      thumb.appendChild(im);
    }
    row.appendChild(thumb);

    var info = document.createElement('div');
    info.className = 'outfit-cfg__gsize-info';
    var name = document.createElement('p');
    name.className = 'outfit-cfg__gsize-name';
    name.textContent = product.title || '';
    info.appendChild(name);

    var meta = [];
    var colour = colourNameOf(product, item.colorCode);
    if (colour) meta.push(colour);
    var lenTag = pieceLength(product);
    if (lenTag) meta.push(gt(isApron(product) ? 'apron_length_line' : 'length_line', { length: lenTag + ' cm' }));
    var price = priceLineFor(product, item);
    if (price) meta.push(price);
    var metaEl = document.createElement('p');
    metaEl.className = 'outfit-cfg__gsize-meta';
    metaEl.textContent = meta.join(' · ');
    info.appendChild(metaEl);

    var acts = document.createElement('div');
    acts.className = 'outfit-cfg__gsize-acts';
    if (stepIdx > -1) acts.appendChild(guideTextButton(gt('change'), function() { goStep(stepIdx, true); }));
    acts.appendChild(guideTextButton(gt('remove'), function() {
      var idx = getState().indexOf(item);
      if (idx > -1) removeItem(idx);
    }));
    info.appendChild(acts);

    // Schnitt first when the colour comes in more than one (Lederhosen, men's jackets)
    var cuts = [];
    entries.forEach(function(e) { if (cuts.indexOf(e.cut) === -1) cuts.push(e.cut); });
    var activeCut = cuts[0];
    if (cuts.length > 1) {
      var curV = variantById(product, item.variant_id);
      // nothing chosen yet: open on the first Schnitt that has a size to sell (94085: every Standardgröße is sold out)
      var openCut = entries.filter(function(e) { return variantAvailable(product, e.variant); }).map(function(e) { return e.cut; })[0];
      activeCut = guide.cuts[item.handle] || (item.sizeSet && curV ? cutOfVariant(product, curV) : (openCut || cuts[0]));
      var cutBox = document.createElement('div');
      cutBox.className = 'outfit-cfg__gsize-cuts';
      cuts.forEach(function(c) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'outfit-cfg__guide-chip';
        b.textContent = c;
        b.setAttribute('aria-pressed', c === activeCut ? 'true' : 'false');
        b.addEventListener('click', function() { guide.cuts[item.handle] = c; renderGuideSizes(); });
        cutBox.appendChild(b);
      });
      info.appendChild(cutBox);
    }

    if (entries.length > 1) {
      var sizesBox = document.createElement('div');
      sizesBox.className = 'outfit-cfg__gsize-sizes';
      entries.forEach(function(e) {
        if (cuts.length > 1 && e.cut !== activeCut) return;
        var v = e.variant;
        var avail = variantAvailable(product, v);
        var chosen = !!item.sizeSet && item.variant_id === v.id;
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'outfit-cfg__size-option' + (avail ? '' : ' is-unavailable') + (chosen ? ' is-selected' : '');
        b.textContent = e.size;
        b.setAttribute('aria-pressed', chosen ? 'true' : 'false');
        b.setAttribute('data-gsize-key', item.handle + '|' + v.id);
        if (!avail) b.setAttribute('aria-disabled', 'true');
        b.addEventListener('click', function(e) {
          if (!avail) return;
          item.variant_id = v.id;
          markSizeChosen(item);
          // the row is redrawn: keyboard users keep their place (a tap has detail >= 1 and needs no ring)
          guide.focusKey = e.detail === 0 ? item.handle + '|' + v.id : '';
          updateUI(); // total, URL, panel and this step in one pass
        });
        sizesBox.appendChild(b);
      });
      info.appendChild(sizesBox);
    }

    var noteEl = document.createElement('p');
    noteEl.className = 'outfit-cfg__gsize-note';
    if (missing) noteEl.textContent = gt('size_missing');
    else if (item.sizeSet && item.sizeFrom) {
      noteEl.textContent = item.sizeFrom.derived
        ? gt('preselected_derived', { size: item.sizeFrom.size, derived: item.sizeFrom.derived })
        : gt('preselected', { size: item.sizeFrom.size });
    }
    info.appendChild(noteEl);
    row.appendChild(info);
    return row;
  }

  function outfitSig() {
    return getState().map(function(it) { return it.handle + ':' + it.variant_id; }).join(',');
  }

  function renderGuideCartFoot(foot) {
    var st = getState();
    if (st.length) {
      var total = document.createElement('p');
      total.className = 'outfit-cfg__guide-total';
      var tl = document.createElement('span');
      tl.textContent = gt('total');
      var tv = document.createElement('strong');
      tv.textContent = money(outfitTotal());
      total.appendChild(tl);
      total.appendChild(tv);
      foot.appendChild(total);
    }
    var missing = st.filter(needsSize).length;
    var cart = document.createElement('button');
    cart.type = 'button';
    cart.className = 'btn outfit-cfg__guide-cta';
    cart.textContent = missing ? gt('choose_sizes_count', { count: missing }) : gt('to_cart');
    cart.disabled = !st.length;
    cart.addEventListener('click', function() { guideAddToCart(cart); });
    var wish = document.createElement('button');
    wish.type = 'button';
    wish.className = 'btn btn--secondary outfit-cfg__guide-cta';
    wish.textContent = gt('to_wishlist');
    wish.disabled = !st.length;
    wish.addEventListener('click', guideToWishlist);
    var status = document.createElement('p');
    status.className = 'outfit-cfg__guide-status';
    status.setAttribute('aria-live', 'polite');
    if (guide.status === 'wish' && guide.statusSig === outfitSig()) {
      var ok = document.createElement('span');
      ok.textContent = gt('saved');
      var a = document.createElement('a');
      a.className = 'outfit-cfg__guide-link';
      a.href = window.g26url('pages/wishlist');
      a.textContent = gt('wishlist_link');
      status.appendChild(ok);
      status.appendChild(a);
    }
    foot.appendChild(cart);
    foot.appendChild(wish);
    if (st.length) {
      // sharing lives on the last step in the guided path (the free mode keeps its icon in "Ihre Auswahl")
      var share = guideTextButton(gt('share'), onShareClick);
      share.classList.add('outfit-cfg__guide-share');
      status.insertBefore(share, status.firstChild);
    }
    foot.appendChild(status);
  }

  function guideAddToCart(btn) {
    var st = getState();
    if (!st.length) return;
    if (st.some(needsSize)) {
      // every piece needs its size: point at the first one still open instead of guessing a size
      guide.showMissing = true;
      renderGuide();
      var first = guide.el.sizes.querySelector('.outfit-cfg__gsize.is-missing');
      if (first) {
        first.scrollIntoView({ block: 'center', behavior: 'smooth' });
        var tile = first.querySelector('.outfit-cfg__size-option:not(.is-unavailable)');
        if (tile) tile.focus({ preventScroll: true });
      }
      return;
    }
    var label = btn.textContent;
    btn.disabled = true;
    btn.textContent = gt('adding');
    postOutfitLines(
      st.map(function(it) { return cartLineFor(it, it.variant_id); }),
      st.map(function(it) { return it.handle; })
    ).then(function() {
      btn.textContent = label;
      btn.disabled = false;
    }).catch(function(err) {
      console.error('Guided cart add failed:', err);
      btn.textContent = gt('error');
      setTimeout(function() { btn.textContent = label; btn.disabled = false; }, 2000);
    });
  }

  // "Alles auf die Merkliste": the outfit as a saved outfit (Merkliste page, Konto) AND every piece on the
  // Merkliste itself, so the price-drop and back-in-stock notes (r154) watch each one.
  function guideToWishlist() {
    var W = window.Goessl && window.Goessl.wishlist;
    if (!W || !window.Goessl.outfits || !getState().length) return;
    if (!W.isLoggedIn()) { W.promptSignIn(); return; } // r39g (SJ): the Merkliste needs an account
    saveOutfitRecord(currentOutfitId ? null : defaultOutfitName());
    getState().forEach(function(it) { if (!W.hasItem(it.handle)) W.addItem(it.handle); });
    guide.status = 'wish';
    guide.statusSig = outfitSig();
    renderGuideFoot();
  }

  // ═══════════════════════════════════════════
  //  PUBLIC API
  // ═══════════════════════════════════════════

  window.Goessl = window.Goessl || {};
  window.Goessl.configurator = {
    getShareUrl: getShareUrl,
    getState: function() { return getState(); },
    getCurrentGender: function() { return currentGender; },
    resetPositions: resetPositions,
    clearCanvas: clearCanvas,
    // Public helpers for the quick-view modal so it can drop a product onto the canvas
    selectProduct: function(product, variant) {
      var collection = guessCollectionForProduct(product);
      var colorCode = null;
      if (variant && variant.option1) colorCode = extractColorCode(variant.option1);
      // r71: the quick view hands over a /products/{h}.js-shaped product —
      // images are URL strings and prices are cents, but the configurator
      // dereferences images[i].src and formats euro strings (renderCanvas
      // TypeError'd and money() would show cents ×100). Normalize a copy.
      var norm = Object.assign({}, product);
      norm.images = (product.images || []).map(function(im) {
        return typeof im === 'string' ? { src: im } : im;
      });
      norm.variants = (product.variants || []).map(function(v) {
        var nv = Object.assign({}, v);
        if (typeof nv.price === 'number') nv.price = (nv.price / 100).toFixed(2);
        if (typeof nv.compare_at_price === 'number') nv.compare_at_price = (nv.compare_at_price / 100).toFixed(2);
        return nv;
      });
      product = norm;
      // Cache so renderCanvas can find images
      productCache[product.handle] = product;
      selectProduct(product, collection, colorCode);
      if (variant && variant.id) {
        var s = getState();
        for (var i = 0; i < s.length; i++) {
          // r191: the size was chosen in the quick view - the guided last step shows it as chosen
          if (s[i].handle === product.handle) { s[i].variant_id = variant.id; markSizeChosen(s[i]); break; }
        }
      }
      updateUI();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
